import React, {createContext, useContext, useState} from "react";
import {Timeline, Divider, Button, Modal, Tag, Empty, Card, Descriptions, Alert, Row, Col} from "antd";
import moment from "moment";
import {
    InfoCircleOutlined,
    PlusCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    DollarOutlined,
    CalendarOutlined,
    TeamOutlined,
    EyeOutlined,
    FileTextOutlined,
    ScheduleOutlined,
    InboxOutlined,
    UndoOutlined,
} from "@ant-design/icons";
import {CourseHistoryByCourseIdQuery, CourseHistoryAction, CourseNotVisibleReason, PaymentInterval} from "../../generated/graphql";
import css from "../bookingHistory/BookingHistory.less";
import {renderUpdateDate} from "../bookingHistory/bookingHistoryRenderHelpers";
import TagComponent from "../../views/Tag/Tag";
import {renderAmount, CurrencyShort} from "../../helpers/renderAmount";
import {useCurrency} from "../../helpers/currencyHelper";
import {
    MONEY_FIELDS,
    HIDDEN_FIELDS,
    FIELD_LABELS,
    HistoryRenderOptions,
    renderJsonValue as renderJsonValueShared,
} from "../common/history/historyDiffHelpers";
import RatesChangedCardShared, {extractRatesFromItems as extractRatesFromItemsShared, ExtractedRate} from "../common/history/RatesChangedCard";

/**
 * Lookup-Maps fuer die Aufloesung von Entity-UUIDs zu lesbaren Namen
 */
export type EntityLookupMaps = {
    courseTypes: Record<string, string>;
    locations: Record<string, string>;
    venues: Record<string, string>;
};

/**
 * Struktur fuer erkannte Bulk-Änderungs-Patterns bei Kurseinheiten
 */
type BulkLessonChangePattern = {
    type: "venue" | "location" | "time_shift" | "mixed";
    /** Anzahl der betroffenen Lessons */
    count: number;
    /** Details zur Änderung */
    details: {
        /** Alte venueId (bei venue-Änderung) */
        oldVenueId?: string;
        /** Neue venueId (bei venue-Änderung) */
        newVenueId?: string;
        /** Alte locationId (bei location-Änderung) */
        oldLocationId?: string;
        /** Neue locationId (bei location-Änderung) */
        newLocationId?: string;
        /** Zeitverschiebung in Minuten (bei time_shift) */
        timeShiftMinutes?: number;
        /** Alte Startzeit (Beispiel) */
        oldStartTime?: string;
        /** Neue Startzeit (Beispiel) */
        newStartTime?: string;
    };
};

/**
 * Felder die Entity-UUIDs enthalten und aufgeloest werden sollen
 */
const UUID_LOOKUP_FIELDS = {
    courseTypeId: "courseTypes",
    locationId: "locations",
    venueId: "venues",
} as const;

/**
 * Context fuer die Lookup-Maps - ermoeglicht Zugriff in verschachtelten Komponenten
 */
const LookupMapsContext = createContext<EntityLookupMaps | undefined>(undefined);

/**
 * Hook zum Zugriff auf die Lookup-Maps
 */
const useLookupMaps = (): EntityLookupMaps | undefined => {
    return useContext(LookupMapsContext);
};

/**
 * Kontext-Daten fuer die Preisberechnung bei "pro Kurseinheit"
 */
type PriceCalculationContext = {
    lessonsCount: number;
    currentPaymentInterval: PaymentInterval | undefined;
};

/**
 * Context fuer Price-Calculation-Daten - ermoeglicht Zugriff auf lessonsCount und paymentInterval
 * in verschachtelten Komponenten fuer die Gesamtpreisberechnung bei "pro Kurseinheit"
 */
const PriceCalculationContext = createContext<PriceCalculationContext | undefined>(undefined);

/**
 * Hook zum Zugriff auf die Price-Calculation-Daten
 */
const usePriceCalculation = (): PriceCalculationContext | undefined => {
    return useContext(PriceCalculationContext);
};

type CourseHistoryItem = CourseHistoryByCourseIdQuery["courseHistoryByCourseId"][number];

// Gruppierte Items (mehrere gleichzeitige Änderungen gleichen Typs)
type GroupedHistoryItems = {
    isGroup: true;
    items: CourseHistoryItem[];
    primaryAction: CourseHistoryAction;
    createDate: string;
    userName: string | null | undefined;
    user: CourseHistoryItem["user"];
    /** Erkanntes Bulk-Änderungs-Pattern (falls vorhanden) */
    bulkPattern?: BulkLessonChangePattern;
};

/**
 * Speichervorgang-Gruppe: Fasst ALLE Änderungen zusammen, die bei einem
 * einzigen Speichervorgang entstanden sind (gleicher Zeitstempel + gleicher User)
 */
type SaveOperationGroup = {
    isSaveOperation: true;
    /** Alle Items dieses Speichervorgangs */
    items: Array<CourseHistoryItem>;
    /** Nach Aktionstyp gruppierte Sub-Einträge */
    actionGroups: Map<CourseHistoryAction, Array<CourseHistoryItem>>;
    /** Zeitstempel des Speichervorgangs */
    createDate: string;
    /** User-ID */
    userId: string | null | undefined;
    /** Username für Anzeige */
    userName: string | null | undefined;
    /** User-Objekt für Details */
    user: CourseHistoryItem["user"];
    /** Alle unterschiedlichen Aktionstypen in diesem Speichervorgang */
    actions: Array<CourseHistoryAction>;
};

// Einzelnes Item, Gruppe oder Speichervorgang
type HistoryItemOrGroup = CourseHistoryItem | GroupedHistoryItems | SaveOperationGroup;

const isGroupedItems = (item: HistoryItemOrGroup): item is GroupedHistoryItems => {
    return "isGroup" in item && item.isGroup === true;
};

const isSaveOperationGroup = (item: HistoryItemOrGroup): item is SaveOperationGroup => {
    return "isSaveOperation" in item && item.isSaveOperation === true;
};

type HistoryItemProps = {
    item: CourseHistoryItem;
};

// Icon und Farbe für CourseHistory-Aktionstypen
type ActionConfig = {
    icon: React.ReactNode;
    color: string;
};

/**
 * Formatiert die Beschreibung für bessere Lesbarkeit:
 * - Konvertiert US-Datumsformat (MM/DD/YY) zu deutschem Format (DD.MM.YY)
 * - Kürzt lange Index-Listen (#1, #2, #3, ...) zu kompakter Darstellung
 */
const formatDescription = (description: string | null | undefined): string => {
    if (!description) {
        return "";
    }

    let formatted = description;

    // 1. US-Datumsformat (MM/DD/YY) zu deutschem Format (DD.MM.YY) konvertieren
    // Matches: (01/30/26), 01/30/26, Fällig: 11/23/26, etc.
    formatted = formatted.replace(/\((\d{2})\/(\d{2})\/(\d{2})\)/g, (_, month, day, year) => `(${day}.${month}.${year})`);
    formatted = formatted.replace(/(\d{2})\/(\d{2})\/(\d{4})/g, (_, month, day, year) => `${day}.${month}.${year}`);
    // Format: "Fällig: MM/DD/YY" -> "Fällig: DD.MM.YY"
    formatted = formatted.replace(/(Fällig:\s*)(\d{2})\/(\d{2})\/(\d{2})/g, (_, prefix, month, day, year) => `${prefix}${day}.${month}.${year}`);

    // 2. Lange Index-Listen kürzen
    // Pattern: (#1, #2, #3, #4, ...) - wenn mehr als 3 Indizes
    const indexListPattern = /\(#(\d+(?:,\s*#\d+){3,})\)/g;

    formatted = formatted.replace(indexListPattern, (match) => {
        // Zähle die Anzahl der Indizes
        const indices = match.match(/#\d+/g);

        if (indices && indices.length > 3) {
            return ""; // Entferne die lange Liste komplett
        }
        
        return match;
    });

    // 3. Redundante Leerzeichen entfernen
    formatted = formatted.replace(/\s+/g, " ").trim();

    return formatted;
};

/**
 * Analysiert ob alle Items in einer Gruppe das gleiche Änderungs-Pattern haben
 * z.B. alle haben die gleiche venueId-Änderung oder die gleiche Zeitverschiebung
 */
const detectBulkLessonPattern = (items: CourseHistoryItem[]): BulkLessonChangePattern | undefined => {
    if (items.length < 2) {
        return undefined;
    }

    // Nur CourseLessonUpdated Items analysieren
    const lessonUpdates = items.filter((item) => item.action === CourseHistoryAction.CourseLessonUpdated);

    if (lessonUpdates.length < 2) {
        return undefined;
    }

    // Extrahiere oldValues und newValues aus allen Items
    const changes: Array<{
        oldVenueId?: string;
        newVenueId?: string;
        oldLocationId?: string;
        newLocationId?: string;
        timeShiftMs?: number;
        oldStartTime?: string;
        newStartTime?: string;
    }> = [];

    for (const item of lessonUpdates) {
        const oldValues = item.oldValues as Record<string, unknown> | undefined;
        const newValues = item.newValues as Record<string, unknown> | undefined;

        if (!oldValues && !newValues) {
            continue;
        }

        const change: typeof changes[number] = {};

        // Venue-Änderung erkennen
        if (oldValues && "venueId" in oldValues) {
            change.oldVenueId = oldValues.venueId as string;
        }
        if (newValues && "venueId" in newValues) {
            change.newVenueId = newValues.venueId as string;
        }

        // Location-Änderung erkennen
        if (oldValues && "locationId" in oldValues) {
            change.oldLocationId = oldValues.locationId as string;
        }
        if (newValues && "locationId" in newValues) {
            change.newLocationId = newValues.locationId as string;
        }

        // Zeit-Verschiebung erkennen
        if (oldValues && newValues && "startDateTime" in oldValues && "startDateTime" in newValues) {
            const oldStart = new Date(oldValues.startDateTime as string);
            const newStart = new Date(newValues.startDateTime as string);

            if (!isNaN(oldStart.getTime()) && !isNaN(newStart.getTime())) {
                change.timeShiftMs = newStart.getTime() - oldStart.getTime();
                change.oldStartTime = oldValues.startDateTime as string;
                change.newStartTime = newValues.startDateTime as string;
            }
        }

        changes.push(change);
    }

    if (changes.length < 2) {
        return undefined;
    }

    // Pruefe auf einheitliche Venue-Änderung
    const venueChanges = changes.filter((c) => c.oldVenueId && c.newVenueId);
    if (venueChanges.length === changes.length) {
        const allSameVenue = venueChanges.every(
            (c) => c.oldVenueId === venueChanges[0].oldVenueId && c.newVenueId === venueChanges[0].newVenueId
        );
        if (allSameVenue && venueChanges[0].oldVenueId !== venueChanges[0].newVenueId) {
            return {
                type: "venue",
                count: venueChanges.length,
                details: {
                    oldVenueId: venueChanges[0].oldVenueId,
                    newVenueId: venueChanges[0].newVenueId,
                },
            };
        }
    }

    // Pruefe auf einheitliche Location-Änderung
    const locationChanges = changes.filter((c) => c.oldLocationId && c.newLocationId);
    if (locationChanges.length === changes.length) {
        const allSameLocation = locationChanges.every(
            (c) => c.oldLocationId === locationChanges[0].oldLocationId && c.newLocationId === locationChanges[0].newLocationId
        );
        if (allSameLocation && locationChanges[0].oldLocationId !== locationChanges[0].newLocationId) {
            return {
                type: "location",
                count: locationChanges.length,
                details: {
                    oldLocationId: locationChanges[0].oldLocationId,
                    newLocationId: locationChanges[0].newLocationId,
                },
            };
        }
    }

    // Pruefe auf einheitliche Zeitverschiebung
    const timeShiftChanges = changes.filter((c) => c.timeShiftMs !== undefined && c.timeShiftMs !== 0);
    if (timeShiftChanges.length === changes.length && timeShiftChanges.length > 0) {
        const allSameShift = timeShiftChanges.every((c) => c.timeShiftMs === timeShiftChanges[0].timeShiftMs);
        if (allSameShift) {
            const shiftMinutes = Math.round((timeShiftChanges[0].timeShiftMs || 0) / (1000 * 60));
            return {
                type: "time_shift",
                count: timeShiftChanges.length,
                details: {
                    timeShiftMinutes: shiftMinutes,
                    oldStartTime: timeShiftChanges[0].oldStartTime,
                    newStartTime: timeShiftChanges[0].newStartTime,
                },
            };
        }
    }

    // Gemischte Änderungen
    if (changes.length > 0) {
        return {
            type: "mixed",
            count: changes.length,
            details: {},
        };
    }

    return undefined;
};

/**
 * Gruppiert History-Einträge nach Speichervorgang (gleicher Zeitstempel + gleicher User).
 * Einträge die innerhalb von 1 Sekunde vom gleichen User erstellt wurden,
 * werden als ein Speichervorgang zusammengefasst.
 *
 * Dies ermöglicht eine übersichtlichere Darstellung wenn z.B. Preise, Lektionen
 * und Raten gleichzeitig geändert wurden.
 */
const groupBySaveOperation = (items: CourseHistoryItem[]): HistoryItemOrGroup[] => {
    if (items.length === 0) return [];

    const result: HistoryItemOrGroup[] = [];
    let i = 0;

    while (i < items.length) {
        const currentItem = items[i];
        const currentTime = moment(currentItem.createDate);
        const currentUserId = currentItem.userId;

        // Sammle alle Items vom gleichen User innerhalb von 1 Sekunde
        const saveOpItems: CourseHistoryItem[] = [currentItem];
        let j = i + 1;

        while (j < items.length) {
            const nextItem = items[j];
            const nextTime = moment(nextItem.createDate);
            const timeDiff = Math.abs(currentTime.diff(nextTime, "milliseconds"));

            // Gruppiere wenn: gleicher User UND innerhalb von 1000ms (1 Sekunde)
            // Hinweis: Bei einem Speichervorgang haben alle Events normalerweise
            // exakt den gleichen Timestamp (Millisekunden-Genauigkeit)
            if (nextItem.userId === currentUserId && timeDiff <= 1000) {
                saveOpItems.push(nextItem);
                j++;
            } else {
                break;
            }
        }

        // Prüfe ob wir verschiedene Aktionstypen haben
        const uniqueActions = new Set(saveOpItems.map((item) => item.action));

        // Nur als SaveOperationGroup darstellen wenn:
        // - Mehr als 1 verschiedener Aktionstyp ODER
        // - Mehr als 3 Items (auch bei gleichem Typ)
        if (uniqueActions.size > 1 || saveOpItems.length > 3) {
            // Gruppiere nach Aktionstyp für die Darstellung
            const actionGroups = new Map<CourseHistoryAction, CourseHistoryItem[]>();
            saveOpItems.forEach((item) => {
                const existing = actionGroups.get(item.action) || [];
                actionGroups.set(item.action, [...existing, item]);
            });

            result.push({
                isSaveOperation: true,
                items: saveOpItems,
                actionGroups,
                createDate: currentItem.createDate,
                userId: currentUserId,
                userName: currentItem.userName,
                user: currentItem.user,
                actions: Array.from(uniqueActions),
            });
        } else {
            // Einzeln hinzufügen (wenige Items vom gleichen Typ)
            saveOpItems.forEach((item) => result.push(item));
        }

        i = j;
    }

    return result;
};

/**
 * Formatiert eine Zeitverschiebung in lesbare Form
 * @param minutes - Verschiebung in Minuten (positiv = später, negativ = frueher)
 */
const formatTimeShift = (minutes: number): string => {
    const absMinutes = Math.abs(minutes);
    const direction = minutes > 0 ? "nach hinten" : "nach vorne";

    if (absMinutes < 60) {
        return `${absMinutes} Minuten ${direction}`;
    }

    const hours = Math.floor(absMinutes / 60);
    const remainingMinutes = absMinutes % 60;

    if (remainingMinutes === 0) {
        return `${hours} Stunde${hours > 1 ? "n" : ""} ${direction}`;
    }

    return `${hours}:${remainingMinutes.toString().padStart(2, "0")} Stunden ${direction}`;
};

/**
 * Extrahiert nur die Uhrzeit aus einem ISO-Datetime-String
 */
const extractTimeFromDateTime = (dateTimeStr: string): string => {
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) {
        return "";
    }
    return moment(date).format("HH:mm");
};

/**
 * Generiert eine Zusammenfassungs-Beschreibung für gruppierte Items
 * Nutzt Bulk-Pattern fuer intelligente Zusammenfassungen bei Kurseinheiten
 */
const getGroupSummary = (group: GroupedHistoryItems, lookupMaps?: EntityLookupMaps): string => {
    const count = group.items.length;
    const action = group.primaryAction;
    const pattern = group.bulkPattern;

    // Wenn ein Bulk-Pattern erkannt wurde, generiere eine intelligente Zusammenfassung
    if (pattern && action === CourseHistoryAction.CourseLessonUpdated) {
        switch (pattern.type) {
            case "location": {
                const oldLocationName = lookupMaps && pattern.details.oldLocationId
                    ? lookupMaps.locations[pattern.details.oldLocationId] || pattern.details.oldLocationId
                    : pattern.details.oldLocationId || "unbekannt";
                const newLocationName = lookupMaps && pattern.details.newLocationId
                    ? lookupMaps.locations[pattern.details.newLocationId] || pattern.details.newLocationId
                    : pattern.details.newLocationId || "unbekannt";

                return `Alle ${pattern.count} Kurseinheiten: Standort geändert von "${oldLocationName}" zu "${newLocationName}"`;
            }

            case "venue": {
                const oldVenueName = lookupMaps && pattern.details.oldVenueId
                    ? lookupMaps.venues[pattern.details.oldVenueId] || pattern.details.oldVenueId
                    : pattern.details.oldVenueId || "unbekannt";
                const newVenueName = lookupMaps && pattern.details.newVenueId
                    ? lookupMaps.venues[pattern.details.newVenueId] || pattern.details.newVenueId
                    : pattern.details.newVenueId || "unbekannt";

                return `Alle ${pattern.count} Kurseinheiten: Raum geändert von "${oldVenueName}" zu "${newVenueName}"`;
            }

            case "time_shift": {
                const shiftMinutes = pattern.details.timeShiftMinutes || 0;
                const shiftDescription = formatTimeShift(shiftMinutes);

                // Falls wir Beispielzeiten haben, diese auch anzeigen
                if (pattern.details.oldStartTime && pattern.details.newStartTime) {
                    const oldTime = extractTimeFromDateTime(pattern.details.oldStartTime);
                    const newTime = extractTimeFromDateTime(pattern.details.newStartTime);

                    if (oldTime && newTime) {
                        return `Alle ${pattern.count} Kurseinheiten ${shiftDescription} verschoben (z.B. ${oldTime} -> ${newTime})`;
                    }
                }

                return `Alle ${pattern.count} Kurseinheiten ${shiftDescription} verschoben`;
            }

            case "mixed":
                return `${pattern.count} Kurseinheiten mit verschiedenen Änderungen`;

            default:
                break;
        }
    }

    // Standard-Zusammenfassungen
    switch (action) {
        case CourseHistoryAction.InstallmentRateUpdated:
            return `${count} Ratenzahlungen geändert`;
        case CourseHistoryAction.InstallmentRateAdded:
        case CourseHistoryAction.InstallmentRatesAdded:
            return `${count} Ratenzahlungen hinzugefuegt`;
        case CourseHistoryAction.InstallmentRateDeleted:
        case CourseHistoryAction.InstallmentRatesDeleted:
            return `${count} Ratenzahlungen entfernt`;
        case CourseHistoryAction.InstallmentRatesRecalculated:
            return `Ratenzahlungen neu berechnet`;
        case CourseHistoryAction.CourseLessonUpdated:
            return `${count} Kurseinheiten geändert`;
        case CourseHistoryAction.CourseLessonAdded:
        case CourseHistoryAction.CourseLessonsAdded:
            return `${count} Kurseinheiten hinzugefuegt`;
        case CourseHistoryAction.CourseLessonDeleted:
        case CourseHistoryAction.CourseLessonsDeleted:
            return `${count} Kurseinheiten entfernt`;
        case CourseHistoryAction.CoursePriceUpdated:
        case CourseHistoryAction.CoursePricesBulkChanged:
            return `${count} Preisänderungen`;
        case CourseHistoryAction.CourseUpdate:
        case CourseHistoryAction.Updated:
            return `${count} Kursänderungen`;
        default:
            return `${count} Änderungen`;
    }
};

/**
 * Lokaler Wrapper um {@link extractRatesFromItemsShared}, damit der bisherige
 * Aufruf-Stil (Items mit voller CourseHistoryItem-Typisierung) erhalten bleibt.
 * Die eigentliche Implementierung lebt in
 * `src/client/containers/common/history/RatesChangedCard.tsx`.
 */
const extractRatesFromItems = (items: CourseHistoryItem[]) => extractRatesFromItemsShared(items);

/**
 * Generiert eine Zusammenfassung für einen gesamten Speichervorgang
 * (mehrere verschiedene Aktionstypen in einer Speicherung)
 */
const getSaveOperationSummary = (saveOp: SaveOperationGroup, currency: CurrencyShort, lookupMaps?: EntityLookupMaps): string[] => {
    const summaries: string[] = [];

    // Sortiere Aktionen nach Wichtigkeit
    const actionOrder: CourseHistoryAction[] = [
        CourseHistoryAction.CourseUpdate,
        CourseHistoryAction.VisibilityChanged,
        CourseHistoryAction.CoursePricesBulkChanged,
        CourseHistoryAction.CoursePriceAdded,
        CourseHistoryAction.CoursePriceUpdated,
        CourseHistoryAction.CoursePriceDeleted,
        CourseHistoryAction.CourseLessonAdded,
        CourseHistoryAction.CourseLessonsAdded,
        CourseHistoryAction.CourseLessonUpdated,
        CourseHistoryAction.CourseLessonDeleted,
        CourseHistoryAction.CourseLessonsDeleted,
        CourseHistoryAction.InstallmentRateAdded,
        CourseHistoryAction.InstallmentRatesAdded,
        CourseHistoryAction.InstallmentRateUpdated,
        CourseHistoryAction.InstallmentRateDeleted,
        CourseHistoryAction.InstallmentRatesDeleted,
        CourseHistoryAction.InstallmentRatesRecalculated,
        CourseHistoryAction.InstructorsChanged,
    ];

    const sortedActions = Array.from(saveOp.actionGroups.keys()).sort((a, b) => {
        const indexA = actionOrder.indexOf(a);
        const indexB = actionOrder.indexOf(b);

        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    sortedActions.forEach((action) => {
        const items = saveOp.actionGroups.get(action) ?? [];

        if (items.length === 0) return;

        // Versuche Bulk-Pattern zu erkennen für bessere Zusammenfassung
        if (action === CourseHistoryAction.CourseLessonUpdated && items.length > 1) {
            const bulkPattern = detectBulkLessonPattern(items);

            if (bulkPattern && bulkPattern.type !== "mixed") {
                const patternSummary = getGroupSummary(
                    {
                        isGroup: true,
                        items,
                        primaryAction: action,
                        createDate: saveOp.createDate,
                        userName: saveOp.userName,
                        user: saveOp.user,
                        bulkPattern,
                    },
                    lookupMaps
                );
                summaries.push(patternSummary);

                return;
            }
        }

        // Standard-Zusammenfassung basierend auf Aktionstyp und Anzahl
        switch (action) {
            case CourseHistoryAction.CourseUpdate:
            case CourseHistoryAction.Updated: {
                // Bei Kurs-Updates: Zeige geänderte Felder
                const changedFields = new Set<string>();

                items.forEach((item) => {
                    const oldVals = item.oldValues as Record<string, unknown> | undefined;
                    const newVals = item.newValues as Record<string, unknown> | undefined;

                    if (oldVals) Object.keys(oldVals).forEach((k) => changedFields.add(k));
                    if (newVals) Object.keys(newVals).forEach((k) => changedFields.add(k));
                });

                const labels = Array.from(changedFields)
                    .filter((f) => !["id"].includes(f))
                    .map((f) => FIELD_LABELS[f] ?? f)
                    .slice(0, 3);

                if (labels.length > 0) {
                    const suffix = changedFields.size > 3 ? ` (+${changedFields.size - 3} weitere)` : "";

                    summaries.push(`Kurs: ${labels.join(", ")}${suffix}`);
                }
                break;
            }

            case CourseHistoryAction.CoursePricesBulkChanged:
            case CourseHistoryAction.CoursePriceUpdated: {
                // Zeige Preisänderungen kompakt
                const priceChanges: Array<string> = [];

                items.forEach((item) => {
                    if (item.description) {
                        priceChanges.push(formatDescription(item.description));
                    }
                });

                if (priceChanges.length === 1) {
                    summaries.push(`Preise: ${priceChanges[0]}`);
                } else if (priceChanges.length > 1) {
                    summaries.push(`${priceChanges.length} Preisänderungen`);
                }
                break;
            }

            case CourseHistoryAction.CourseLessonUpdated:
                summaries.push(`${items.length} Kurseinheiten geändert`);
                break;

            case CourseHistoryAction.CourseLessonAdded:
            case CourseHistoryAction.CourseLessonsAdded:
                summaries.push(`${items.length} Kurseinheit${items.length > 1 ? "en" : ""} hinzugefügt`);
                break;

            case CourseHistoryAction.CourseLessonDeleted:
            case CourseHistoryAction.CourseLessonsDeleted:
                summaries.push(`${items.length} Kurseinheit${items.length > 1 ? "en" : ""} entfernt`);
                break;

            case CourseHistoryAction.InstallmentRateUpdated:
                summaries.push(`${items.length} Rate${items.length > 1 ? "n" : ""} geändert`);
                break;

            case CourseHistoryAction.InstallmentRateAdded:
            case CourseHistoryAction.InstallmentRatesAdded: {
                // Prüfe ob es auch DELETE gibt - dann ist es eine Raten-Neuberechnung
                const hasDelete = saveOp.actionGroups.has(CourseHistoryAction.InstallmentRatesDeleted) ||
                    saveOp.actionGroups.has(CourseHistoryAction.InstallmentRateDeleted);
                if (hasDelete) {
                    // Kombinierte Anzeige - Zusammenfassung wird bei DELETE generiert
                    break;
                }
                summaries.push(`${items.length} Rate${items.length > 1 ? "n" : ""} hinzugefügt`);
                break;
            }

            case CourseHistoryAction.InstallmentRateDeleted:
            case CourseHistoryAction.InstallmentRatesDeleted: {
                // Prüfe ob es auch ADD gibt - dann ist es eine Raten-Neuberechnung
                const addedItems = saveOp.actionGroups.get(CourseHistoryAction.InstallmentRatesAdded) ||
                    saveOp.actionGroups.get(CourseHistoryAction.InstallmentRateAdded);
                if (addedItems && addedItems.length > 0) {
                    // Extrahiere alte und neue Raten-Infos für kombinierte Zusammenfassung
                    const oldRates = extractRatesFromItems(items);
                    const newRates = extractRatesFromItems(addedItems);
                    const oldTotal = oldRates.reduce((sum, r) => sum + (r.amount || 0), 0);
                    const newTotal = newRates.reduce((sum, r) => sum + (r.amount || 0), 0);
                    summaries.push(`Raten geändert (${oldRates.length} → ${newRates.length} Raten, Summe: ${renderAmount({amount: oldTotal, currency})} → ${renderAmount({amount: newTotal, currency})})`);
                } else {
                    summaries.push(`${items.length} Rate${items.length > 1 ? "n" : ""} entfernt`);
                }
                break;
            }

            case CourseHistoryAction.InstallmentRatesRecalculated: {
                // Neues kombiniertes Event vom Backend - enthält alte und neue Raten
                const oldValues = items[0]?.oldValues as Record<string, unknown> | undefined;
                const newValues = items[0]?.newValues as Record<string, unknown> | undefined;
                const oldRatesArray = (oldValues?.rates as Array<unknown>) ?? [];
                const newRatesArray = (newValues?.rates as Array<unknown>) ?? [];
                const oldTotal = Number(oldRatesArray.reduce((sum: number, r) => {
                    const rate = r as Record<string, unknown>;
                    return sum + (Number(rate.installmentAmount) || 0);
                }, 0)) || 0;
                const newTotal = Number(newRatesArray.reduce((sum: number, r) => {
                    const rate = r as Record<string, unknown>;
                    return sum + (Number(rate.installmentAmount) || 0);
                }, 0)) || 0;
                summaries.push(`Raten neu berechnet (${oldRatesArray.length} → ${newRatesArray.length} Raten, Summe: ${renderAmount({amount: oldTotal, currency})} → ${renderAmount({amount: newTotal, currency})})`);
                break;
            }

            case CourseHistoryAction.InstructorsChanged:
                summaries.push("Kursleiter geändert");
                break;

            case CourseHistoryAction.VisibilityChanged: {
                // Einträge zur Buchungsbeschränkung tragen eine sprechende Beschreibung (#1083)
                const description = items.length === 1 ? items[0].description : undefined;

                summaries.push(description && /^(Buchungsbeschränkung|Freigabekurs)/.test(description) ? description : "Sichtbarkeit geändert");
                break;
            }

            default:
                if (items.length > 1) {
                    summaries.push(`${items.length} ${action}-Änderungen`);
                } else if (items[0]?.description) {
                    summaries.push(formatDescription(items[0].description));
                }
        }
    });

    return summaries;
};

const getActionConfig = (action: CourseHistoryAction): ActionConfig => {
    switch (action) {
        // Kurs erstellen/löschen/archivieren
        case CourseHistoryAction.CourseCreate:
            return {icon: <PlusCircleOutlined />, color: "green"};
        case CourseHistoryAction.CourseDelete:
            return {icon: <DeleteOutlined />, color: "red"};
        case CourseHistoryAction.CourseArchive:
            return {icon: <InboxOutlined />, color: "orange"};
        case CourseHistoryAction.CourseUnarchive:
            return {icon: <UndoOutlined />, color: "green"};

        // Kurs aktualisieren
        case CourseHistoryAction.CourseUpdate:
        case CourseHistoryAction.Updated:
            return {icon: <EditOutlined />, color: "blue"};

        // Sichtbarkeit
        case CourseHistoryAction.VisibilityChanged:
            return {icon: <EyeOutlined />, color: "purple"};

        // Kurspreis
        case CourseHistoryAction.CoursePriceAdded:
            return {icon: <DollarOutlined />, color: "green"};
        case CourseHistoryAction.CoursePriceUpdated:
        case CourseHistoryAction.CoursePricesBulkChanged:
            return {icon: <DollarOutlined />, color: "gold"};
        case CourseHistoryAction.CoursePriceDeleted:
            return {icon: <DollarOutlined />, color: "red"};

        // Kurseinheiten
        case CourseHistoryAction.CourseLessonAdded:
        case CourseHistoryAction.CourseLessonsAdded:
            return {icon: <CalendarOutlined />, color: "green"};
        case CourseHistoryAction.CourseLessonUpdated:
            return {icon: <CalendarOutlined />, color: "blue"};
        case CourseHistoryAction.CourseLessonDeleted:
        case CourseHistoryAction.CourseLessonsDeleted:
            return {icon: <CalendarOutlined />, color: "red"};

        // Ratenzahlungen
        case CourseHistoryAction.InstallmentRateAdded:
        case CourseHistoryAction.InstallmentRatesAdded:
            return {icon: <ScheduleOutlined />, color: "green"};
        case CourseHistoryAction.InstallmentRateUpdated:
            return {icon: <ScheduleOutlined />, color: "gold"};
        case CourseHistoryAction.InstallmentRateDeleted:
        case CourseHistoryAction.InstallmentRatesDeleted:
            return {icon: <ScheduleOutlined />, color: "red"};
        case CourseHistoryAction.InstallmentRatesRecalculated:
            return {icon: <ScheduleOutlined />, color: "gold"};

        // Kursleiter
        case CourseHistoryAction.InstructorsChanged:
            return {icon: <TeamOutlined />, color: "purple"};

        // Unbekannt
        case CourseHistoryAction.Unknown:
        default:
            return {icon: <InfoCircleOutlined />, color: "default"};
    }
};

/**
 * Generiert einen kurzen, lesbaren Titel fuer das Modal basierend auf der Aktion
 */
const getModalTitle = (action: CourseHistoryAction): string => {
    const actionTitles: Record<CourseHistoryAction, string> = {
        [CourseHistoryAction.CourseCreate]: "Kurs erstellt",
        [CourseHistoryAction.CourseUpdate]: "Kursänderung",
        [CourseHistoryAction.Updated]: "Änderungsdetails",
        [CourseHistoryAction.CourseDelete]: "Kurs geloescht",
        [CourseHistoryAction.CourseArchive]: "Kurs archiviert",
        [CourseHistoryAction.CourseUnarchive]: "Kurs wiederhergestellt",
        [CourseHistoryAction.VisibilityChanged]: "Sichtbarkeit geändert",
        [CourseHistoryAction.CoursePriceAdded]: "Preis hinzugefuegt",
        [CourseHistoryAction.CoursePriceUpdated]: "Preisänderung",
        [CourseHistoryAction.CoursePriceDeleted]: "Preis entfernt",
        [CourseHistoryAction.CoursePricesBulkChanged]: "Preisänderungen",
        [CourseHistoryAction.CourseLessonAdded]: "Kurseinheit hinzugefuegt",
        [CourseHistoryAction.CourseLessonsAdded]: "Kurseinheiten hinzugefuegt",
        [CourseHistoryAction.CourseLessonUpdated]: "Kurseinheit geändert",
        [CourseHistoryAction.CourseLessonDeleted]: "Kurseinheit entfernt",
        [CourseHistoryAction.CourseLessonsDeleted]: "Kurseinheiten entfernt",
        [CourseHistoryAction.InstallmentRateAdded]: "Rate hinzugefuegt",
        [CourseHistoryAction.InstallmentRatesAdded]: "Raten hinzugefuegt",
        [CourseHistoryAction.InstallmentRateUpdated]: "Rate geändert",
        [CourseHistoryAction.InstallmentRateDeleted]: "Rate entfernt",
        [CourseHistoryAction.InstallmentRatesDeleted]: "Raten entfernt",
        [CourseHistoryAction.InstallmentRatesRecalculated]: "Raten neu berechnet",
        [CourseHistoryAction.InstructorsChanged]: "Kursleiter geändert",
        [CourseHistoryAction.Unknown]: "Änderungsdetails",
    };

    return actionTitles[action] || "Änderungsdetails";
};

/*
 * Konstanten fuer Feld-Mappings (MONEY_FIELDS, VAT_FIELDS, HIDDEN_FIELDS,
 * FIELD_LABELS, PAYMENT_TYPE_LABELS) wurden nach
 * `src/client/containers/common/history/historyDiffHelpers.tsx` extrahiert
 * und dort als Source-of-Truth gepflegt. Die Course-Historie nutzt die
 * Default-Werte unveraendert; die Booking-Historie erweitert sie ueber
 * HistoryRenderOptions.
 */

// Details-Modal für einen History-Eintrag
const CourseHistoryDetailsModal: React.FC<{item: CourseHistoryItem; open: boolean; onClose: () => void}> = ({item, open, onClose}) => {
    // Einträge zur Buchungsbeschränkung laufen technisch als Sichtbarkeitsänderung (#1083)
    const modalTitle = /^(Buchungsbeschränkung|Freigabekurs)/.test(item.description ?? "") ? "Buchungsbeschränkung" : getModalTitle(item.action);
    const currencySettings = useCurrency();
    const lookupMaps = useLookupMaps();

    /**
     * Loest eine UUID zu einem lesbaren Namen auf, falls moeglich
     * @param fieldName - Der Name des Feldes (z.B. "courseTypeId")
     * @param uuid - Die UUID die aufgeloest werden soll
     * @returns Der aufgeloeste Name oder die UUID falls nicht aufloesbar
     */
    const resolveUuidToName = (fieldName: string, uuid: string): string => {
        if (!lookupMaps) {
            return uuid;
        }

        const lookupKey = UUID_LOOKUP_FIELDS[fieldName as keyof typeof UUID_LOOKUP_FIELDS];
        if (!lookupKey) {
            return uuid;
        }

        const lookupMap = lookupMaps[lookupKey];
        return lookupMap[uuid] || uuid;
    };

    // Render-Optionen fuer die geteilten Helfer (UUID-Aufloesung wird nur in
    // der Course-Historie genutzt, damit Course-Verhalten unveraendert bleibt).
    const renderOptions: HistoryRenderOptions = {
        currencySettings,
        resolveUuid: resolveUuidToName,
        uuidLookupFields: Object.keys(UUID_LOOKUP_FIELDS),
    };

    const renderJsonValue = (value: unknown, depth = 0, fieldName?: string): React.ReactNode =>
        renderJsonValueShared(value, renderOptions, depth, fieldName);

    // Der Sichtbarkeits-Snapshot ist ein technisches Objekt (reason, inTimeRange ...): als lesbare Tags
    // zeigen statt als Rohdaten. Felder der Buchungsbeschränkung in der Reihenfolge des Formulars
    // (FIELD_LABELS) statt alphabetisch nach technischem Schlüssel.
    const renderValues = (values: unknown): React.ReactNode => {
        const {visibility, ...rest} = values as Record<string, unknown>;
        const restrictionKeys = Object.keys(FIELD_LABELS).filter((key) => key.startsWith("restriction") && key in rest);
        const other: Record<string, unknown> = {};

        Object.keys(rest)
            .filter((key) => restrictionKeys.indexOf(key) === -1)
            .forEach((key) => {
                other[key] = rest[key];
            });

        return (
            <>
                {Object.keys(other).length > 0 && renderJsonValue(other)}
                {restrictionKeys.map((key) => (
                    <div key={key} style={{marginBottom: 4}}>
                        <strong>{FIELD_LABELS[key]}:</strong> {renderJsonValue(Array.isArray(rest[key]) && (rest[key] as Array<unknown>).length === 0 ? null : rest[key], 1, key)}
                    </div>
                ))}
                {visibility && typeof visibility === "object" ? (
                    <div style={{marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap"}}>
                        <strong>{FIELD_LABELS.visibility}:</strong>
                        {renderVisibilityInfo(visibility as Record<string, unknown>)}
                    </div>
                ) : null}
            </>
        );
    };

    return (
        <Modal open={open} onCancel={onClose} title={modalTitle} width={800} footer={null}>
            {/* Meta-Informationen: Datum und Beschreibung */}
            <div style={{marginBottom: 16, padding: "8px 12px", backgroundColor: "#fafafa", borderRadius: 4}}>
                <div style={{fontSize: "0.85em", color: "#666", marginBottom: 4}}>
                    {moment(item.createDate).format("DD.MM.YYYY [um] HH:mm [Uhr]")}
                </div>
                {item.description && (
                    <div style={{fontSize: "0.9em", color: "#333"}}>
                        {formatDescription(item.description)}
                    </div>
                )}
            </div>
            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24}}>
                {hasValues(item.oldValues) && (
                    <div>
                        <h4 style={{borderBottom: "1px solid #f0f0f0", paddingBottom: 8}}>Vorher</h4>
                        {renderValues(item.oldValues)}
                    </div>
                )}
                {hasValues(item.newValues) && (
                    <div>
                        <h4 style={{borderBottom: "1px solid #f0f0f0", paddingBottom: 8}}>Nachher</h4>
                        {renderValues(item.newValues)}
                    </div>
                )}
            </div>
            {!item.oldValues && !item.newValues && <Empty description="Keine Details verfügbar" />}
        </Modal>
    );
};

// HistoryItem-Komponente
const HistoryItem: React.FC<HistoryItemProps> = ({item}) => {
    const [detailsOpen, setDetailsOpen] = useState(false);

    const getUserInfo = (user: CourseHistoryItem["user"]) => {
        if (!user) {
            return item.userName ?? "System";
        }

        const userInfo = user.instructorDetails ? `${user.instructorDetails.firstname} ${user.instructorDetails.lastname}` : user.email ?? item.userName ?? "N/A";

        return userInfo;
    };

    const actionConfig = getActionConfig(item.action);

    // Farbzuordnung für Timeline-Dot (Ant Design Farben)
    const getDotStyle = (color: string) => {
        const colorMap: Record<string, {bg: string; border: string; icon: string}> = {
            green: {bg: "#f6ffed", border: "#52c41a", icon: "#389e0d"},
            red: {bg: "#fff1f0", border: "#ff4d4f", icon: "#cf1322"},
            orange: {bg: "#fff7e6", border: "#fa8c16", icon: "#d46b08"},
            blue: {bg: "#e6f7ff", border: "#1890ff", icon: "#0958d9"},
            purple: {bg: "#f9f0ff", border: "#722ed1", icon: "#531dab"},
            gold: {bg: "#fffbe6", border: "#faad14", icon: "#d48806"},
            cyan: {bg: "#e6fffb", border: "#13c2c2", icon: "#08979c"},
            geekblue: {bg: "#f0f5ff", border: "#2f54eb", icon: "#1d39c4"},
            default: {bg: "#fafafa", border: "#d9d9d9", icon: "#595959"},
        };

        return colorMap[color] ?? colorMap.default;
    };

    const dotStyle = getDotStyle(actionConfig.color);
    const hasDetails = item.oldValues || item.newValues;

    return (
        <>
            <Timeline.Item
                key={item.id}
                className={css.historyItem}
                dot={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: dotStyle.bg,
                            border: `2px solid ${dotStyle.border}`,
                        }}
                    >
                        <span style={{color: dotStyle.icon, fontSize: "14px", display: "flex", alignItems: "center"}}>{actionConfig.icon}</span>
                    </div>
                }
            >
                <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4}}>
                    <Tag icon={actionConfig.icon} color={actionConfig.color} style={{margin: 0, fontSize: "0.95em", whiteSpace: "normal", height: "auto"}}>
                        {formatDescription(item.description) || item.action}
                    </Tag>
                </div>
                <div className={css.entryMedium + " " + css.grey}>{renderUpdateDate(item.createDate, true)}</div>
                <div className={css.subSubLine}>
                    <TagComponent color="cyan" className={css.subSubLineItem}>
                        {getUserInfo(item.user)}
                    </TagComponent>
                    {hasDetails && (
                        <Button type="link" size="small" onClick={() => setDetailsOpen(true)} style={{padding: 0}} className={css.subSubLineItem}>
                            <InfoCircleOutlined />
                        </Button>
                    )}
                </div>
            </Timeline.Item>
            <CourseHistoryDetailsModal item={item} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
        </>
    );
};

// GroupedHistoryItem-Komponente für gruppierte Einträge
type GroupedHistoryItemProps = {
    group: GroupedHistoryItems;
};

const GroupedHistoryItem: React.FC<GroupedHistoryItemProps> = ({group}) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const lookupMaps = useLookupMaps();
    const currencySettings = useCurrency();

    const getUserInfo = (user: CourseHistoryItem["user"]) => {
        if (!user) {
            return group.userName ?? "System";
        }

        const userInfo = user.instructorDetails ? `${user.instructorDetails.firstname} ${user.instructorDetails.lastname}` : user.email ?? group.userName ?? "N/A";

        return userInfo;
    };

    const actionConfig = getActionConfig(group.primaryAction);

    const getDotStyle = (color: string) => {
        const colorMap: Record<string, {bg: string; border: string; icon: string}> = {
            green: {bg: "#f6ffed", border: "#52c41a", icon: "#389e0d"},
            red: {bg: "#fff1f0", border: "#ff4d4f", icon: "#cf1322"},
            orange: {bg: "#fff7e6", border: "#fa8c16", icon: "#d46b08"},
            blue: {bg: "#e6f7ff", border: "#1890ff", icon: "#0958d9"},
            purple: {bg: "#f9f0ff", border: "#722ed1", icon: "#531dab"},
            gold: {bg: "#fffbe6", border: "#faad14", icon: "#d48806"},
            cyan: {bg: "#e6fffb", border: "#13c2c2", icon: "#08979c"},
            geekblue: {bg: "#f0f5ff", border: "#2f54eb", icon: "#1d39c4"},
            default: {bg: "#fafafa", border: "#d9d9d9", icon: "#595959"},
        };

        return colorMap[color] ?? colorMap.default;
    };

    const dotStyle = getDotStyle(actionConfig.color);
    const summary = getGroupSummary(group, lookupMaps);

    /**
     * Loest eine UUID zu einem lesbaren Namen auf
     */
    const resolveUuid = (fieldName: string, uuid: string): string => {
        if (!lookupMaps) {
            return uuid;
        }

        const lookupKey = UUID_LOOKUP_FIELDS[fieldName as keyof typeof UUID_LOOKUP_FIELDS];
        if (!lookupKey) {
            return uuid;
        }

        const lookupMap = lookupMaps[lookupKey];
        return lookupMap[uuid] || uuid;
    };

    /**
     * Rendert einen einzelnen Wert aus oldValues/newValues lesbar
     */
    const renderValue = (fieldName: string, value: unknown): string => {
        if (value === null || value === undefined) {
            return "-";
        }

        // UUID-Felder aufloesen
        if (fieldName in UUID_LOOKUP_FIELDS && typeof value === "string") {
            return resolveUuid(fieldName, value);
        }

        // Datum/Zeit formatieren
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
            return moment(value).format("DD.MM.YYYY HH:mm");
        }

        // Geldbeträge
        if (MONEY_FIELDS.includes(fieldName) && typeof value === "number") {
            return renderAmount({amount: value, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"});
        }

        // PaymentInterval (Kurspreisberechnung)
        if (fieldName === "paymentInterval" && typeof value === "string") {
            if (value === "PerCourse") return "pro Kurs";
            if (value === "PerLesson") return "pro Kurseinheit";
        }

        return String(value);
    };

    return (
        <>
            <Timeline.Item
                key={`group-${group.createDate}`}
                className={css.historyItem}
                dot={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: dotStyle.bg,
                            border: `2px solid ${dotStyle.border}`,
                        }}
                    >
                        <span style={{color: dotStyle.icon, fontSize: "14px", display: "flex", alignItems: "center"}}>{actionConfig.icon}</span>
                    </div>
                }
            >
                <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4}}>
                    <Tag icon={actionConfig.icon} color={actionConfig.color} style={{margin: 0, fontSize: "0.95em", whiteSpace: "normal", height: "auto"}}>
                        {summary}
                    </Tag>
                </div>
                <div className={css.entryMedium + " " + css.grey}>{renderUpdateDate(group.createDate, true)}</div>
                <div className={css.subSubLine}>
                    <TagComponent color="cyan" className={css.subSubLineItem}>
                        {getUserInfo(group.user)}
                    </TagComponent>
                    <Button type="link" size="small" onClick={() => setDetailsOpen(true)} style={{padding: 0}} className={css.subSubLineItem}>
                        <InfoCircleOutlined /> Details ({group.items.length})
                    </Button>
                </div>
            </Timeline.Item>
            <Modal
                open={detailsOpen}
                onCancel={() => setDetailsOpen(false)}
                title={
                    <div style={{display: "flex", alignItems: "center", gap: 12}}>
                        <Tag icon={actionConfig.icon} color={actionConfig.color} style={{margin: 0}}>
                            {group.items.length} Kurseinheiten
                        </Tag>
                        <span style={{fontWeight: 500}}>{getModalTitle(group.primaryAction)}</span>
                    </div>
                }
                width={800}
                footer={null}
            >
                {/* Meta-Informationen: Datum und User */}
                <div style={{marginBottom: 20, color: "#666", fontSize: "0.9em"}}>
                    {moment(group.createDate).format("DD.MM.YYYY [um] HH:mm [Uhr]")}
                    {group.user && (
                        <span style={{marginLeft: 8}}>
                            - {getUserInfo(group.user)}
                        </span>
                    )}
                </div>

                {/* Bei erkanntem Bulk-Pattern: Alert mit Zusammenfassung */}
                {group.bulkPattern && group.bulkPattern.type !== "mixed" && (
                    <Alert
                        type="info"
                        showIcon
                        style={{marginBottom: 20}}
                        message="Einheitliche Änderung erkannt"
                        description={summary}
                    />
                )}

                {/* Timeline mit den einzelnen Kurseinheiten */}
                <div style={{maxHeight: "55vh", overflowY: "auto", paddingRight: 8}}>
                    <Timeline style={{paddingTop: 8}}>
                        {/* Sortiere Items chronologisch nach startDateTime (älteste zuerst) */}
                        {[...group.items]
                            .sort((a, b) => {
                                const oldValsA = a.oldValues as Record<string, unknown> | undefined;
                                const newValsA = a.newValues as Record<string, unknown> | undefined;
                                const oldValsB = b.oldValues as Record<string, unknown> | undefined;
                                const newValsB = b.newValues as Record<string, unknown> | undefined;

                                // Extrahiere startDateTime aus oldValues oder newValues
                                const dateStrA = (oldValsA?.startDateTime || newValsA?.startDateTime) as string | undefined;
                                const dateStrB = (oldValsB?.startDateTime || newValsB?.startDateTime) as string | undefined;

                                if (!dateStrA && !dateStrB) return 0;
                                if (!dateStrA) return 1;
                                if (!dateStrB) return -1;

                                const dateA = new Date(dateStrA);
                                const dateB = new Date(dateStrB);

                                return dateA.getTime() - dateB.getTime();
                            })
                            .map((item, idx) => {
                                const oldVals = item.oldValues as Record<string, unknown> | undefined;
                                const newVals = item.newValues as Record<string, unknown> | undefined;

                                // Extrahiere Datum aus oldValues fuer den Card-Titel
                                const lessonDate = oldVals && oldVals.startDateTime
                                    ? moment(oldVals.startDateTime as string).format("DD.MM.YY")
                                    : "";

                                // Sammle alle geänderten Felder fuer Descriptions
                                const changedFields: Array<{field: string; label: string; oldVal: string; newVal: string}> = [];

                                if (oldVals || newVals) {
                                    // Definiere die gewuenschte Feld-Reihenfolge
                                    const fieldOrder = ["startDateTime", "endDateTime", "locationId", "venueId"];
                                    const allFields = new Set([
                                        ...Object.keys(oldVals || {}),
                                        ...Object.keys(newVals || {}),
                                    ]);

                                    // Sortiere Felder
                                    const sortedFields = Array.from(allFields).sort((keyA, keyB) => {
                                        const indexA = fieldOrder.indexOf(keyA);
                                        const indexB = fieldOrder.indexOf(keyB);

                                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                                        if (indexA !== -1) return -1;
                                        if (indexB !== -1) return 1;
                                        
                                        return keyA.localeCompare(keyB);
                                    });

                                    sortedFields.forEach((field) => {
                                        if (HIDDEN_FIELDS.includes(field)) {
                                            return;
                                        }
                                        const oldVal = oldVals ? oldVals[field] : undefined;
                                        const newVal = newVals ? newVals[field] : undefined;

                                        if (oldVal !== newVal) {
                                            changedFields.push({
                                                field,
                                                label: FIELD_LABELS[field] || field,
                                                oldVal: renderValue(field, oldVal),
                                                newVal: renderValue(field, newVal),
                                            });
                                        }
                                    });
                                }

                                return (
                                    <Timeline.Item
                                        key={item.id}
                                        color={actionConfig.color}
                                    >
                                        <Card
                                            size="small"
                                            title={
                                                <span style={{fontWeight: 500}}>
                                                    Kurseinheit #{idx + 1}
                                                    {lessonDate && (
                                                        <span style={{fontWeight: 400, marginLeft: 8, color: "#666"}}>
                                                            ({lessonDate})
                                                        </span>
                                                    )}
                                                </span>
                                            }
                                            style={{marginBottom: 0}}
                                            bodyStyle={{padding: changedFields.length > 0 ? 12 : 0}}
                                        >
                                            {changedFields.length > 0 && (
                                                <Descriptions
                                                    column={2}
                                                    size="small"
                                                    bordered
                                                    labelStyle={{backgroundColor: "#fafafa", fontWeight: 500, width: "25%"}}
                                                >
                                                    {changedFields.map(({field, label, oldVal, newVal}) => (
                                                        <React.Fragment key={field}>
                                                            <Descriptions.Item
                                                                label={
                                                                    <span>
                                                                        {label}
                                                                        <Tag color="default" style={{marginLeft: 8, fontSize: "0.75em"}}>Vorher</Tag>
                                                                    </span>
                                                                }
                                                            >
                                                                <span style={{color: "#999"}}>{oldVal}</span>
                                                            </Descriptions.Item>
                                                            <Descriptions.Item
                                                                label={
                                                                    <span>
                                                                        {label}
                                                                        <Tag color="blue" style={{marginLeft: 8, fontSize: "0.75em"}}>Nachher</Tag>
                                                                    </span>
                                                                }
                                                            >
                                                                <span style={{color: "#0760FF", fontWeight: 500}}>{newVal}</span>
                                                            </Descriptions.Item>
                                                        </React.Fragment>
                                                    ))}
                                                </Descriptions>
                                            )}
                                        </Card>
                                    </Timeline.Item>
                                );
                            })}
                    </Timeline>
                </div>
            </Modal>
        </>
    );
};

/**
 * Optionen fuer die Preisberechnung bei "pro Kurseinheit"
 */
type PriceExtractionOptions = {
    /** Anzahl der Kurseinheiten */
    lessonsCount: number;
    /** PaymentInterval aus dem alten Zustand (falls in der Aenderung enthalten) */
    oldPaymentInterval?: string;
    /** PaymentInterval aus dem neuen Zustand (falls in der Aenderung enthalten) */
    newPaymentInterval?: string;
    /** Aktueller PaymentInterval des Kurses (Fallback wenn nicht in der Aenderung) */
    currentPaymentInterval?: string;
};

/**
 * Formatiert einen Preis mit optionaler Gesamtpreis-Berechnung bei "pro Kurseinheit"
 * @param unitPrice - Der Preis pro Einheit
 * @param lessonsCount - Anzahl der Kurseinheiten
 * @param paymentInterval - "PerLesson" oder "PerCourse"
 * @param currencySettings - Waehrungseinstellungen
 * @returns Formatierter String, z.B. "150,00 EUR (15 x 10,00 EUR)" bei PerLesson
 */
const formatPriceWithCalculation = (
    unitPrice: number,
    lessonsCount: number,
    paymentInterval: string | undefined,
    currencySettings: {short: string; symbol: string}
): string => {
    const formattedUnitPrice = renderAmount({
        amount: unitPrice,
        currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"
    });

    // Bei "pro Kurseinheit" den Gesamtpreis berechnen und Details anzeigen
    if (paymentInterval === "PerLesson" && lessonsCount > 0) {
        const totalPrice = unitPrice * lessonsCount;
        const formattedTotalPrice = renderAmount({
            amount: totalPrice,
            currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"
        });
        // Format: "150,00 EUR (15 x 10,00 EUR)"
        return `${formattedTotalPrice} (${lessonsCount} x ${formattedUnitPrice})`;
    }

    return formattedUnitPrice;
};

/**
 * Extrahiert Preisänderungen aus den oldValues/newValues eines Preis-Items
 * und gibt eine lesbare Zusammenfassung zurück.
 *
 * Bei "pro Kurseinheit" (PerLesson) wird der Gesamtpreis berechnet und
 * mit Berechnungsdetails angezeigt (z.B. "150,00 EUR (15 x 10,00 EUR)")
 */
const extractPriceChanges = (
    oldVals: Record<string, unknown> | undefined,
    newVals: Record<string, unknown> | undefined,
    currencySettings: {short: string; symbol: string},
    options?: PriceExtractionOptions
): Array<{label: string; oldVal: string; newVal: string}> => {
    const changes: Array<{label: string; oldVal: string; newVal: string}> = [];

    // Prüfe ob prices als Array oder als flache Felder vorhanden sind
    const oldPrices = oldVals?.prices as Array<Record<string, unknown>> | undefined;
    const newPrices = newVals?.prices as Array<Record<string, unknown>> | undefined;

    // Falls prices ein Array ist, extrahiere das erste Element
    const oldPrice = oldPrices?.[0] ?? oldVals;
    const newPrice = newPrices?.[0] ?? newVals;

    // Bestimme den effektiven PaymentInterval fuer old/new
    // Prioritaet: explizit in Aenderung > aktueller Wert vom Kurs > undefined
    const effectiveOldPaymentInterval = options?.oldPaymentInterval ?? options?.currentPaymentInterval;
    const effectiveNewPaymentInterval = options?.newPaymentInterval ?? options?.currentPaymentInterval;
    const lessonsCount = options?.lessonsCount ?? 0;

    // Definiere relevante Preis-Felder in gewünschter Reihenfolge
    const priceFields = [
        {key: "grossPrice", label: "Kurspreis"},
        {key: "netPrice", label: "Nettopreis"},
        {key: "vat", label: "MwSt"},
        {key: "discount", label: "Rabatt"},
        {key: "discountUnit", label: "Rabatteinheit"},
    ];

    priceFields.forEach(({key, label}) => {
        const oldVal = oldPrice?.[key];
        const newVal = newPrice?.[key];

        if (oldVal === newVal || (oldVal === undefined && newVal === undefined)) {
            return;
        }

        let oldStr = "-";
        let newStr = "-";

        if (key === "vat") {
            // VAT als Prozent
            oldStr = oldVal !== undefined ? `${Math.round(Number(oldVal) * 100)}%` : "-";
            newStr = newVal !== undefined ? `${Math.round(Number(newVal) * 100)}%` : "-";
        } else if (key === "discountUnit") {
            // Rabatteinheit übersetzen
            oldStr = oldVal === "Percentage" ? "%" : oldVal === "Currency" ? currencySettings.symbol : String(oldVal ?? "-");
            newStr = newVal === "Percentage" ? "%" : newVal === "Currency" ? currencySettings.symbol : String(newVal ?? "-");
        } else if (MONEY_FIELDS.includes(key)) {
            // Geldbeträge - mit Gesamtpreis-Berechnung bei PerLesson
            if (oldVal !== undefined) {
                oldStr = formatPriceWithCalculation(
                    Number(oldVal),
                    lessonsCount,
                    effectiveOldPaymentInterval,
                    currencySettings
                );
            }
            if (newVal !== undefined) {
                newStr = formatPriceWithCalculation(
                    Number(newVal),
                    lessonsCount,
                    effectiveNewPaymentInterval,
                    currencySettings
                );
            }
        } else {
            oldStr = oldVal !== undefined ? String(oldVal) : "-";
            newStr = newVal !== undefined ? String(newVal) : "-";
        }

        changes.push({label, oldVal: oldStr, newVal: newStr});
    });

    return changes;
};

/**
 * Extrahiert Kursleiter-Namen aus den instructors/instructorIds Feldern
 */
const extractInstructorChanges = (
    oldVals: Record<string, unknown> | undefined,
    newVals: Record<string, unknown> | undefined
): Array<{label: string; oldVal: string; newVal: string}> => {
    const changes: Array<{label: string; oldVal: string; newVal: string}> = [];

    // Versuche Instructor-Namen zu extrahieren
    const getInstructorNames = (vals: Record<string, unknown> | undefined): string => {
        if (!vals) return "-";

        // Prüfe ob instructors als Array mit Objekten vorhanden ist
        const instructors = vals.instructors as Array<{firstname?: string; lastname?: string; name?: string}> | undefined;

        if (Array.isArray(instructors) && instructors.length > 0) {
            const names = instructors.map((i) => {
                if (i.firstname && i.lastname) return `${i.firstname} ${i.lastname}`;
                if (i.name) return i.name;

                return "Unbekannt";
            });

            return names.join(", ") || "-";
        }

        // Prüfe ob es ein einzelnes Instructor-Objekt ist
        if (vals.firstname && vals.lastname) {
            return `${vals.firstname} ${vals.lastname}`;
        }

        // Fallback: Anzahl der IDs
        const instructorIds = vals.instructorIds as Array<string> | undefined;

        if (Array.isArray(instructorIds)) {
            if (instructorIds.length === 0) return "-";

            return `${instructorIds.length} Kursleiter`;
        }

        return "-";
    };

    const oldNames = getInstructorNames(oldVals);
    const newNames = getInstructorNames(newVals);

    if (oldNames !== newNames) {
        changes.push({
            label: "Kursleiter",
            oldVal: oldNames,
            newVal: newNames,
        });
    }

    return changes;
};

/**
 * Extrahiert Ratenzahlungs-Änderungen aus oldValues/newValues
 * Gibt lesbare Änderungen mit Betrag, Fälligkeitsdatum und Typ zurück
 */
const extractInstallmentRateChanges = (
    oldVals: Record<string, unknown> | undefined,
    newVals: Record<string, unknown> | undefined,
    currencySettings: {short: string; symbol: string}
): Array<{label: string; oldVal: string; newVal: string}> => {
    const changes: Array<{label: string; oldVal: string; newVal: string}> = [];

    // Definiere relevante Raten-Felder in gewünschter Reihenfolge
    const rateFields = [
        {key: "installmentAmount", label: "Betrag"},
        {key: "installmentDue", label: "Fällig am"},
        {key: "installmentType", label: "Ratentyp"},
    ];

    // Übersetzung für installmentType
    const typeLabels: Record<string, string> = {
        Standard: "Standard",
        Discount: "Ermäßigt",
        Custom: "Benutzerdefiniert",
    };

    rateFields.forEach(({key, label}) => {
        const oldVal = oldVals?.[key];
        const newVal = newVals?.[key];

        if (oldVal === newVal || (oldVal === undefined && newVal === undefined)) {
            return;
        }

        let oldStr = "-";
        let newStr = "-";

        if (key === "installmentAmount") {
            // Geldbetrag formatieren
            oldStr = oldVal !== undefined
                ? renderAmount({amount: Number(oldVal), currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"})
                : "-";
            newStr = newVal !== undefined
                ? renderAmount({amount: Number(newVal), currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"})
                : "-";
        } else if (key === "installmentDue") {
            // Datum formatieren
            oldStr = oldVal !== undefined ? moment(oldVal as string).format("DD.MM.YYYY") : "-";
            newStr = newVal !== undefined ? moment(newVal as string).format("DD.MM.YYYY") : "-";
        } else if (key === "installmentType") {
            // Typ übersetzen
            oldStr = oldVal !== undefined ? typeLabels[String(oldVal)] || String(oldVal) : "-";
            newStr = newVal !== undefined ? typeLabels[String(newVal)] || String(newVal) : "-";
        } else {
            oldStr = oldVal !== undefined ? String(oldVal) : "-";
            newStr = newVal !== undefined ? String(newVal) : "-";
        }

        changes.push({label, oldVal: oldStr, newVal: newStr});
    });

    return changes;
};

const VISIBILITY_REASON_LABELS: Record<CourseNotVisibleReason, string> = {
    [CourseNotVisibleReason.HideAfterDays]: "Ausgeblendet (Tage überschritten)",
    [CourseNotVisibleReason.MembersOnly]: "Nur für Mitglieder",
    [CourseNotVisibleReason.PrerequisiteCourses]: "Voraussetzungskurse erforderlich",
    [CourseNotVisibleReason.LocationIsPublic]: "Standort nicht öffentlich",
    [CourseNotVisibleReason.CourseShowInWeb]: "Kurs nicht im Web angezeigt",
    [CourseNotVisibleReason.CourseTypeShowInWeb]: "Kursart nicht im Web angezeigt",
    [CourseNotVisibleReason.HideAfterRestrictionEnd]: "Ausgeblendet nach Buchungsende",
    [CourseNotVisibleReason.IsArchived]: "Archiviert",
};

// Beim Anlegen/Löschen einer Buchungsbeschränkung ist eine Seite komplett leer (#1083): Block dann weglassen
const hasValues = (values: unknown): boolean => Boolean(values) && Object.values(values as Record<string, unknown>).some((value) => value !== null);

// Felder der Buchungsbeschränkung in einem Historien-Eintrag (#1083): Wert als Text
const formatRestrictionValue = (value: unknown): string => {
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        return "–";
    }
    if (typeof value === "boolean") {
        return value ? "Ja" : "Nein";
    }
    if (Array.isArray(value)) {
        return value.join(", ");
    }

    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value) ? moment(value).format("DD.MM.YYYY HH:mm") : String(value);
};

const renderVisibilityInfo = (vis: Record<string, unknown> | undefined): React.ReactNode => {
    if (!vis) return <Tag>Unbekannt</Tag>;

    const visibleForAll = vis.visibleForAll as boolean | undefined;
    const reason = vis.reason as string[] | undefined;

    return (
        <div style={{display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center"}}>
            {visibleForAll !== undefined && (
                <Tag color={visibleForAll ? "green" : "red"}>
                    {visibleForAll ? "Sichtbar für alle" : "Eingeschränkt"}
                </Tag>
            )}
            {Array.isArray(reason) && reason.length > 0 && reason.map((r) => (
                <Tag key={r} color="orange">{VISIBILITY_REASON_LABELS[r as CourseNotVisibleReason] ?? r}</Tag>
            ))}
        </div>
    );
};

/**
 * Rendert Items für eine Aktionsgruppe im SaveOperation-Modal
 * Spezielle Behandlung für Preise, Kursleiter, Kurseinheiten und Ratenzahlungen
 */
const renderActionGroupItems = (
    action: CourseHistoryAction,
    items: Array<CourseHistoryItem>,
    renderValue: (fieldName: string, value: unknown) => string,
    lookupMaps: EntityLookupMaps | undefined,
    currencySettings: {short: string; symbol: string},
    priceExtractionOptions?: PriceExtractionOptions
): React.ReactNode => {
    // Spezielle Behandlung für Kursleiter-Änderungen
    if (action === CourseHistoryAction.InstructorsChanged) {
        return items.map((item, idx) => {
            const oldVals = item.oldValues as Record<string, unknown> | undefined;
            const newVals = item.newValues as Record<string, unknown> | undefined;
            const changes = extractInstructorChanges(oldVals, newVals);

            if (changes.length === 0) {
                return (
                    <div
                        key={item.id}
                        style={{
                            padding: "8px 0",
                            borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                        }}
                    >
                        {formatDescription(item.description) || "Kursleiter geändert"}
                    </div>
                );
            }

            return (
                <div
                    key={item.id}
                    style={{
                        padding: "8px 0",
                        borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                    }}
                >
                    {changes.map(({label, oldVal, newVal}) => (
                        <div key={label} style={{display: "flex", gap: 8, fontSize: "0.9em", marginBottom: 4, alignItems: "center"}}>
                            <span style={{fontWeight: 500, minWidth: 120}}>{label}:</span>
                            <span style={{color: "#999"}}>{oldVal}</span>
                            <span style={{color: "#666"}}>→</span>
                            <Tag color="green" style={{margin: 0}}>{newVal}</Tag>
                        </div>
                    ))}
                </div>
            );
        });
    }

    // Spezielle Behandlung für Preis-Änderungen
    if (
        action === CourseHistoryAction.CoursePricesBulkChanged ||
        action === CourseHistoryAction.CoursePriceUpdated ||
        action === CourseHistoryAction.CoursePriceAdded ||
        action === CourseHistoryAction.CoursePriceDeleted
    ) {
        return items.map((item, idx) => {
            const oldVals = item.oldValues as Record<string, unknown> | undefined;
            const newVals = item.newValues as Record<string, unknown> | undefined;
            const changes = extractPriceChanges(oldVals, newVals, currencySettings, priceExtractionOptions);

            if (changes.length === 0) {
                return (
                    <div
                        key={item.id}
                        style={{
                            padding: "8px 0",
                            borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                        }}
                    >
                        {formatDescription(item.description) || "Preisänderung"}
                    </div>
                );
            }

            return (
                <div
                    key={item.id}
                    style={{
                        padding: "8px 0",
                        borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                    }}
                >
                    {changes.map(({label, oldVal, newVal}) => (
                        <div key={label} style={{display: "flex", gap: 8, fontSize: "0.9em", marginBottom: 4, alignItems: "center"}}>
                            <span style={{fontWeight: 500, minWidth: 120}}>{label}:</span>
                            <span style={{color: "#999"}}>{oldVal}</span>
                            <span style={{color: "#666"}}>→</span>
                            <Tag color="green" style={{margin: 0}}>{newVal}</Tag>
                        </div>
                    ))}
                </div>
            );
        });
    }

    // Spezielle Behandlung für Ratenzahlungen
    if (
        action === CourseHistoryAction.InstallmentRateUpdated ||
        action === CourseHistoryAction.InstallmentRateAdded ||
        action === CourseHistoryAction.InstallmentRatesAdded ||
        action === CourseHistoryAction.InstallmentRateDeleted ||
        action === CourseHistoryAction.InstallmentRatesDeleted ||
        action === CourseHistoryAction.InstallmentRatesRecalculated
    ) {
        const isAddAction = action === CourseHistoryAction.InstallmentRateAdded ||
            action === CourseHistoryAction.InstallmentRatesAdded;
        const isDeleteAction = action === CourseHistoryAction.InstallmentRateDeleted ||
            action === CourseHistoryAction.InstallmentRatesDeleted;
        const isRecalculatedAction = action === CourseHistoryAction.InstallmentRatesRecalculated;

        // Spezielle Behandlung für InstallmentRatesRecalculated - kombinierte Anzeige
        if (isRecalculatedAction && items.length > 0) {
            const item = items[0];
            const oldVals = item.oldValues as Record<string, unknown> | undefined;
            const newVals = item.newValues as Record<string, unknown> | undefined;
            const oldRatesArray = (oldVals?.rates as Array<Record<string, unknown>>) ?? [];
            const newRatesArray = (newVals?.rates as Array<Record<string, unknown>>) ?? [];

            // Nutze die RatesChangedCard-ähnliche Darstellung
            // Hilfsfunktion zum Sortieren nach Fälligkeitsdatum
            const sortByDueDate = (rates: ExtractedRate[]): ExtractedRate[] => {
                return [...rates].sort((a, b) => {
                    if (!a.dueDate && !b.dueDate) return 0;
                    if (!a.dueDate) return 1;
                    if (!b.dueDate) return -1;

                    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
                });
            };

            const oldRates = sortByDueDate(oldRatesArray.map((rate) => ({
                amount: rate.installmentAmount as number | undefined,
                dueDate: rate.installmentDue as string | undefined,
                type: rate.installmentType as string | undefined,
            })));
            const newRates = sortByDueDate(newRatesArray.map((rate) => ({
                amount: rate.installmentAmount as number | undefined,
                dueDate: rate.installmentDue as string | undefined,
                type: rate.installmentType as string | undefined,
            })));

            const oldTotal = oldRates.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
            const newTotal = newRates.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

            return (
                <div style={{paddingTop: 8}}>
                    <Row gutter={16}>
                        {/* Alte Raten */}
                        <Col span={12}>
                            <Card
                                size="small"
                                title={<><Tag color="red">Vorher</Tag> {oldRates.length} Raten</>}
                                style={{marginBottom: 8}}
                                bodyStyle={{padding: "8px 12px"}}
                            >
                                <Timeline style={{margin: 0, padding: 0}}>
                                    {oldRates.map((rate, idx) => (
                                        <Timeline.Item key={`old-${idx}`} color="red" style={{paddingBottom: 8}}>
                                            <div style={{fontSize: "0.9em"}}>
                                                <span style={{fontWeight: 500}}>
                                                    {rate.dueDate ? moment(rate.dueDate).format("DD.MM.YYYY") : "-"}
                                                </span>
                                                <span style={{marginLeft: 8}}>
                                                    {rate.amount !== undefined
                                                        ? renderAmount({amount: rate.amount, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"})
                                                        : "-"}
                                                </span>
                                            </div>
                                        </Timeline.Item>
                                    ))}
                                </Timeline>
                                <div style={{borderTop: "1px solid #f0f0f0", paddingTop: 8, marginTop: 8}}>
                                    <strong>Summe:</strong> {renderAmount({amount: oldTotal, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"})}
                                </div>
                            </Card>
                        </Col>
                        {/* Neue Raten */}
                        <Col span={12}>
                            <Card
                                size="small"
                                title={<><Tag color="green">Nachher</Tag> {newRates.length} Raten</>}
                                style={{marginBottom: 8}}
                                bodyStyle={{padding: "8px 12px"}}
                            >
                                <Timeline style={{margin: 0, padding: 0}}>
                                    {newRates.map((rate, idx) => (
                                        <Timeline.Item key={`new-${idx}`} color="green" style={{paddingBottom: 8}}>
                                            <div style={{fontSize: "0.9em"}}>
                                                <span style={{fontWeight: 500}}>
                                                    {rate.dueDate ? moment(rate.dueDate).format("DD.MM.YYYY") : "-"}
                                                </span>
                                                <span style={{marginLeft: 8}}>
                                                    {rate.amount !== undefined
                                                        ? renderAmount({amount: rate.amount, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"})
                                                        : "-"}
                                                </span>
                                            </div>
                                        </Timeline.Item>
                                    ))}
                                </Timeline>
                                <div style={{borderTop: "1px solid #f0f0f0", paddingTop: 8, marginTop: 8}}>
                                    <strong>Summe:</strong> {renderAmount({amount: newTotal, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"})}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            );
        }

        // Helper-Funktion um eine einzelne Rate zu rendern
        const renderSingleRate = (
            rateData: Record<string, unknown>,
            key: string,
            isAdd: boolean,
            isDelete: boolean
        ) => {
            const amount = rateData.installmentAmount as number | undefined;
            const dueDate = rateData.installmentDue as string | undefined;
            const rateType = rateData.installmentType as string | undefined;

            const amountStr = amount !== undefined
                ? renderAmount({amount, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"})
                : "-";
            const dueDateStr = dueDate ? moment(dueDate).format("DD.MM.YYYY") : "-";
            const typeLabel = rateType === "Discount" ? "Ermäßigt" : rateType === "Custom" ? "Benutzerdefiniert" : "Standard";

            return (
                <Timeline.Item
                    key={key}
                    color={isAdd ? "green" : isDelete ? "red" : "gold"}
                >
                    <Card
                        size="small"
                        title={
                            <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                <Tag color={isAdd ? "green" : isDelete ? "red" : "gold"} style={{margin: 0}}>
                                    {isAdd ? "Neu" : isDelete ? "Entfernt" : "Geändert"}
                                </Tag>
                                <span style={{fontWeight: 500}}>{dueDateStr}</span>
                            </div>
                        }
                        style={{marginBottom: 0}}
                        bodyStyle={{padding: "8px 12px"}}
                    >
                        <div style={{display: "flex", gap: 16, fontSize: "0.9em"}}>
                            <div>
                                <span style={{color: "#666"}}>Betrag: </span>
                                <Tag color={isAdd ? "green" : "default"} style={{margin: 0}}>{amountStr}</Tag>
                            </div>
                            {rateType && rateType !== "Standard" && (
                                <div>
                                    <span style={{color: "#666"}}>Typ: </span>
                                    <span>{typeLabel}</span>
                                </div>
                            )}
                        </div>
                    </Card>
                </Timeline.Item>
            );
        };

        // Sammle alle Raten aus allen Items (behandle Bulk- und Einzel-Format)
        const allRates: Array<{data: Record<string, unknown>; key: string; isAdd: boolean; isDelete: boolean}> = [];

        items.forEach((item, itemIdx) => {
            const oldVals = item.oldValues as Record<string, unknown> | undefined;
            const newVals = item.newValues as Record<string, unknown> | undefined;

            if (isAddAction || isDeleteAction) {
                const vals = isAddAction ? newVals : oldVals;

                // Prüfe auf Bulk-Format mit rates-Array
                const ratesArray = vals?.rates as Array<Record<string, unknown>> | undefined;
                if (ratesArray && Array.isArray(ratesArray) && ratesArray.length > 0) {
                    // Bulk-Format: Extrahiere jede Rate aus dem Array
                    ratesArray.forEach((rate, rateIdx) => {
                        allRates.push({
                            data: rate,
                            key: `${item.id}-rate-${rateIdx}`,
                            isAdd: isAddAction,
                            isDelete: isDeleteAction,
                        });
                    });
                } else if (vals?.installmentAmount !== undefined || vals?.installmentDue !== undefined) {
                    // Einzel-Format: Direkte Felder
                    allRates.push({
                        data: vals as Record<string, unknown>,
                        key: item.id,
                        isAdd: isAddAction,
                        isDelete: isDeleteAction,
                    });
                }
            } else {
                // Update-Fall: Zeige Änderungen
                const changes = extractInstallmentRateChanges(oldVals, newVals, currencySettings);
                if (changes.length > 0) {
                    const dueDate = (oldVals?.installmentDue ?? newVals?.installmentDue) as string | undefined;
                    const dueDateStr = dueDate ? moment(dueDate).format("DD.MM.YYYY") : `Rate #${itemIdx + 1}`;
                    allRates.push({
                        data: {
                            _isUpdate: true,
                            _changes: changes,
                            _dueDateStr: dueDateStr,
                        },
                        key: item.id,
                        isAdd: false,
                        isDelete: false,
                    });
                }
            }
        });

        // Sortiere nach Fälligkeitsdatum
        allRates.sort((a, b) => {
            const dueA = a.data.installmentDue as string | undefined;
            const dueB = b.data.installmentDue as string | undefined;

            if (!dueA && !dueB) return 0;
            if (!dueA) return 1;
            if (!dueB) return -1;

            return new Date(dueA).getTime() - new Date(dueB).getTime();
        });

        return (
            <Timeline style={{paddingTop: 8, marginLeft: 8}}>
                {allRates.map(({data, key, isAdd, isDelete}) => {
                    // Update-Fall mit vorbereiteten Änderungen
                    if (data._isUpdate) {
                        const changes = data._changes as Array<{label: string; oldVal: string; newVal: string}>;
                        const dueDateStr = data._dueDateStr as string;
                        return (
                            <Timeline.Item key={key} color="gold">
                                <Card
                                    size="small"
                                    title={
                                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                            <Tag color="gold" style={{margin: 0}}>Geändert</Tag>
                                            <span style={{fontWeight: 500}}>Fällig: {dueDateStr}</span>
                                        </div>
                                    }
                                    style={{marginBottom: 0}}
                                    bodyStyle={{padding: "8px 12px"}}
                                >
                                    {changes.map(({label, oldVal, newVal}) => (
                                        <div
                                            key={label}
                                            style={{
                                                display: "flex",
                                                gap: 8,
                                                fontSize: "0.9em",
                                                marginBottom: 4,
                                                alignItems: "center",
                                            }}
                                        >
                                            <span style={{fontWeight: 500, minWidth: 80}}>{label}:</span>
                                            <span style={{color: "#999"}}>{oldVal}</span>
                                            <span style={{color: "#666"}}>→</span>
                                            <Tag color="green" style={{margin: 0}}>{newVal}</Tag>
                                        </div>
                                    ))}
                                </Card>
                            </Timeline.Item>
                        );
                    }

                    // Add/Delete-Fall
                    return renderSingleRate(data, key, isAdd, isDelete);
                })}
            </Timeline>
        );
    }

    // Spezielle Behandlung für Kurseinheiten
    if (
        action === CourseHistoryAction.CourseLessonUpdated ||
        action === CourseHistoryAction.CourseLessonAdded ||
        action === CourseHistoryAction.CourseLessonsAdded ||
        action === CourseHistoryAction.CourseLessonDeleted ||
        action === CourseHistoryAction.CourseLessonsDeleted
    ) {
        // Prüfe auf Bulk-Pattern
        const bulkPattern = detectBulkLessonPattern(items);
        const hasBulkPattern = bulkPattern && bulkPattern.type !== "mixed";

        // Sortiere nach startDateTime
        const sortedItems = [...items].sort((a, b) => {
            const oldValsA = a.oldValues as Record<string, unknown> | undefined;
            const newValsA = a.newValues as Record<string, unknown> | undefined;
            const oldValsB = b.oldValues as Record<string, unknown> | undefined;
            const newValsB = b.newValues as Record<string, unknown> | undefined;

            const dateStrA = (oldValsA?.startDateTime ?? newValsA?.startDateTime) as string | undefined;
            const dateStrB = (oldValsB?.startDateTime ?? newValsB?.startDateTime) as string | undefined;

            if (!dateStrA && !dateStrB) return 0;
            if (!dateStrA) return 1;
            if (!dateStrB) return -1;

            return new Date(dateStrA).getTime() - new Date(dateStrB).getTime();
        });

        return (
            <>
                {/* Bulk-Pattern Alert */}
                {hasBulkPattern && (
                    <Alert
                        type="info"
                        showIcon
                        style={{marginBottom: 12}}
                        message={
                            bulkPattern.type === "venue"
                                ? `Alle ${bulkPattern.count} Kurseinheiten: Raum geändert`
                                : bulkPattern.type === "location"
                                    ? `Alle ${bulkPattern.count} Kurseinheiten: Standort geändert`
                                    : `Alle ${bulkPattern.count} Kurseinheiten: ${formatTimeShift(bulkPattern.details.timeShiftMinutes ?? 0)} verschoben`
                        }
                        description={
                            bulkPattern.type === "venue" && lookupMaps
                                ? `Von "${lookupMaps.venues[bulkPattern.details.oldVenueId ?? ""] ?? bulkPattern.details.oldVenueId}" zu "${lookupMaps.venues[bulkPattern.details.newVenueId ?? ""] ?? bulkPattern.details.newVenueId}"`
                                : bulkPattern.type === "location" && lookupMaps
                                    ? `Von "${lookupMaps.locations[bulkPattern.details.oldLocationId ?? ""] ?? bulkPattern.details.oldLocationId}" zu "${lookupMaps.locations[bulkPattern.details.newLocationId ?? ""] ?? bulkPattern.details.newLocationId}"`
                                    : bulkPattern.type === "time_shift" && bulkPattern.details.oldStartTime
                                        ? `z.B. ${extractTimeFromDateTime(bulkPattern.details.oldStartTime)} → ${extractTimeFromDateTime(bulkPattern.details.newStartTime ?? "")}`
                                        : undefined
                        }
                    />
                )}

                {/* Einzelne Kurseinheiten */}
                <Timeline style={{paddingTop: 8, marginLeft: 8}}>
                    {sortedItems.map((item, idx) => {
                        const oldVals = item.oldValues as Record<string, unknown> | undefined;
                        const newVals = item.newValues as Record<string, unknown> | undefined;

                        // Extrahiere Datum für Titel
                        const startDateTime = (oldVals?.startDateTime ?? newVals?.startDateTime) as string | undefined;
                        const lessonDate = startDateTime ? moment(startDateTime).format("ddd DD.MM.YYYY") : "";
                        const lessonTime = startDateTime ? moment(startDateTime).format("HH:mm") : "";

                        // Sammle geänderte Felder
                        const changedFields: Array<{label: string; oldVal: string; newVal: string}> = [];

                        if (oldVals || newVals) {
                            const fieldOrder = ["startDateTime", "endDateTime", "locationId", "venueId"];
                            const allFields = new Set([
                                ...Object.keys(oldVals ?? {}),
                                ...Object.keys(newVals ?? {}),
                            ]);

                            const sortedFields = Array.from(allFields).sort((keyA, keyB) => {
                                const indexA = fieldOrder.indexOf(keyA);
                                const indexB = fieldOrder.indexOf(keyB);

                                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                                if (indexA !== -1) return -1;
                                if (indexB !== -1) return 1;

                                return keyA.localeCompare(keyB);
                            });

                            sortedFields.forEach((field) => {
                                if (HIDDEN_FIELDS.includes(field)) return;

                                const oldVal = oldVals?.[field];
                                const newVal = newVals?.[field];

                                if (oldVal !== newVal) {
                                    changedFields.push({
                                        label: FIELD_LABELS[field] ?? field,
                                        oldVal: renderValue(field, oldVal),
                                        newVal: renderValue(field, newVal),
                                    });
                                }
                            });
                        }

                        return (
                            <Timeline.Item key={item.id} color="blue">
                                <Card
                                    size="small"
                                    title={
                                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                            <Tag color="blue" style={{margin: 0}}>#{idx + 1}</Tag>
                                            <span style={{fontWeight: 500}}>{lessonDate}</span>
                                            {lessonTime && (
                                                <span style={{color: "#666", fontWeight: 400}}>
                                                    {lessonTime} Uhr
                                                </span>
                                            )}
                                        </div>
                                    }
                                    style={{marginBottom: 0}}
                                    bodyStyle={{padding: changedFields.length > 0 ? "8px 12px" : 0}}
                                >
                                    {changedFields.map(({label, oldVal, newVal}) => (
                                        <div
                                            key={label}
                                            style={{
                                                display: "flex",
                                                gap: 8,
                                                fontSize: "0.9em",
                                                marginBottom: 4,
                                                alignItems: "center",
                                            }}
                                        >
                                            <span style={{fontWeight: 500, minWidth: 80}}>{label}:</span>
                                            <span style={{color: "#999"}}>{oldVal}</span>
                                            <span style={{color: "#666"}}>→</span>
                                            <Tag color="green" style={{margin: 0}}>{newVal}</Tag>
                                        </div>
                                    ))}
                                </Card>
                            </Timeline.Item>
                        );
                    })}
                </Timeline>
            </>
        );
    }

    // Spezielle Behandlung für Sichtbarkeitsänderungen
    if (action === CourseHistoryAction.VisibilityChanged) {
        return items.map((item, idx) => {
            const oldVals = item.oldValues as Record<string, unknown> | undefined;
            const newVals = item.newValues as Record<string, unknown> | undefined;
            const oldVis = oldVals?.visibility as Record<string, unknown> | undefined;
            const newVis = newVals?.visibility as Record<string, unknown> | undefined;
            const restrictionFields = Object.keys({...oldVals, ...newVals}).filter((field) => field.startsWith("restriction"));

            return (
                <div
                    key={item.id}
                    style={{
                        padding: "8px 0",
                        borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                    }}
                >
                    {item.description && (
                        <div style={{marginBottom: 8, fontWeight: 500}}>
                            {formatDescription(item.description)}
                        </div>
                    )}
                    {oldVis && newVis ? (
                        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                            <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                                <span style={{minWidth: 60, color: "#999"}}>Vorher:</span>
                                {renderVisibilityInfo(oldVis)}
                            </div>
                            <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                                <span style={{minWidth: 60, color: "#666"}}>Nachher:</span>
                                {renderVisibilityInfo(newVis)}
                            </div>
                        </div>
                    ) : newVis ? (
                        <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                            <span style={{minWidth: 60, color: "#666"}}>Status:</span>
                            {renderVisibilityInfo(newVis)}
                        </div>
                    ) : null}
                    {restrictionFields.map((field) => (
                        <div key={field} style={{marginTop: 4}}>
                            <span style={{color: "#666"}}>{FIELD_LABELS[field] ?? field}:</span> {formatRestrictionValue(oldVals?.[field])} → {formatRestrictionValue(newVals?.[field])}
                        </div>
                    ))}
                </div>
            );
        });
    }

    // Standard-Darstellung für andere Aktionstypen
    return items.map((item, idx) => {
        const oldVals = item.oldValues as Record<string, unknown> | undefined;
        const newVals = item.newValues as Record<string, unknown> | undefined;

        // Sammle geänderte Felder
        const changedFields: Array<{
            field: string;
            label: string;
            oldVal: string;
            newVal: string;
        }> = [];

        if (oldVals || newVals) {
            const allFields = new Set([
                ...Object.keys(oldVals ?? {}),
                ...Object.keys(newVals ?? {}),
            ]);

            allFields.forEach((field) => {
                if (HIDDEN_FIELDS.includes(field)) return;

                const oldVal = oldVals ? oldVals[field] : undefined;
                const newVal = newVals ? newVals[field] : undefined;

                if (oldVal !== newVal) {
                    changedFields.push({
                        field,
                        label: FIELD_LABELS[field] ?? field,
                        oldVal: renderValue(field, oldVal),
                        newVal: renderValue(field, newVal),
                    });
                }
            });
        }

        // Wenn keine strukturierten Änderungen, zeige die Beschreibung
        if (changedFields.length === 0) {
            return (
                <div
                    key={item.id}
                    style={{
                        padding: "8px 0",
                        borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                    }}
                >
                    {formatDescription(item.description) || "Änderung"}
                </div>
            );
        }

        return (
            <div
                key={item.id}
                style={{
                    padding: "8px 0",
                    borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                }}
            >
                {changedFields.map(({label, oldVal, newVal}) => (
                    <div key={label} style={{display: "flex", gap: 8, fontSize: "0.9em", marginBottom: 4, alignItems: "center"}}>
                        <span style={{fontWeight: 500, minWidth: 120}}>{label}:</span>
                        <span style={{color: "#999"}}>{oldVal}</span>
                        <span style={{color: "#666"}}>→</span>
                        <Tag color="green" style={{margin: 0}}>{newVal}</Tag>
                    </div>
                ))}
            </div>
        );
    });
};

/**
 * Komponente fuer kombinierte Raten-Aenderungen wurde nach
 * `src/client/containers/common/history/RatesChangedCard.tsx` extrahiert
 * (Wiederverwendung in der Booking-Historie). Wir alias-importieren sie
 * hier weiter als `RatesChangedCard`, damit alle bestehenden Aufrufstellen
 * (siehe SaveOperationGroupItem) unveraendert bleiben.
 */
const RatesChangedCard = RatesChangedCardShared;

// SaveOperationGroupItem-Komponente für gruppierte Speichervorgänge (mehrere verschiedene Aktionen)
type SaveOperationGroupItemProps = {
    saveOp: SaveOperationGroup;
};

const SaveOperationGroupItem: React.FC<SaveOperationGroupItemProps> = ({saveOp}) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const lookupMaps = useLookupMaps();
    const currencySettings = useCurrency();
    const priceCalculation = usePriceCalculation();

    /**
     * Extrahiert paymentInterval-Aenderungen aus den CourseUpdate-Items dieses SaveOperation.
     * Falls paymentInterval geaendert wurde, gibt es old/new Werte zurueck.
     * Falls nicht, wird der currentPaymentInterval vom Kurs als Fallback verwendet.
     */
    const getPaymentIntervalFromSaveOp = (): {oldPaymentInterval?: string; newPaymentInterval?: string} => {
        const courseUpdateItems = saveOp.actionGroups.get(CourseHistoryAction.CourseUpdate) ?? [];

        for (const item of courseUpdateItems) {
            const oldVals = item.oldValues as Record<string, unknown> | undefined;
            const newVals = item.newValues as Record<string, unknown> | undefined;

            // Pruefe ob paymentInterval in dieser Aenderung enthalten ist
            if (oldVals?.paymentInterval !== undefined || newVals?.paymentInterval !== undefined) {
                return {
                    oldPaymentInterval: oldVals?.paymentInterval as string | undefined,
                    newPaymentInterval: newVals?.paymentInterval as string | undefined,
                };
            }
        }

        // Keine paymentInterval-Aenderung gefunden
        return {};
    };

    const paymentIntervalChange = getPaymentIntervalFromSaveOp();

    // Erstelle die PriceExtractionOptions fuer diesen SaveOperation-Kontext
    const priceExtractionOptions: PriceExtractionOptions = {
        lessonsCount: priceCalculation?.lessonsCount ?? 0,
        oldPaymentInterval: paymentIntervalChange.oldPaymentInterval,
        newPaymentInterval: paymentIntervalChange.newPaymentInterval,
        currentPaymentInterval: priceCalculation?.currentPaymentInterval,
    };

    const getUserInfo = (user: CourseHistoryItem["user"]) => {
        if (!user) {
            return saveOp.userName ?? "System";
        }

        const userInfo = user.instructorDetails
            ? `${user.instructorDetails.firstname} ${user.instructorDetails.lastname}`
            : user.email ?? saveOp.userName ?? "N/A";

        return userInfo;
    };

    // Generiere Zusammenfassung
    const summaries = getSaveOperationSummary(saveOp, currencySettings.short as CurrencyShort, lookupMaps);
    const totalItems = saveOp.items.length;
    const actionCount = saveOp.actions.length;

    // Hauptfarbe basierend auf dem "wichtigsten" Aktionstyp
    const getPrimaryActionConfig = () => {
        // Priorität: Updates > Prices > Lessons > Rates > Rest
        const priorityOrder = [
            CourseHistoryAction.CourseUpdate,
            CourseHistoryAction.CoursePricesBulkChanged,
            CourseHistoryAction.CourseLessonUpdated,
            CourseHistoryAction.InstallmentRateUpdated,
        ];

        for (const action of priorityOrder) {
            if (saveOp.actions.includes(action)) {
                return getActionConfig(action);
            }
        }

        return getActionConfig(saveOp.actions[0] || CourseHistoryAction.Unknown);
    };

    const primaryConfig = getPrimaryActionConfig();

    const getDotStyle = (color: string) => {
        const colorMap: Record<string, {bg: string; border: string; icon: string}> = {
            green: {bg: "#f6ffed", border: "#52c41a", icon: "#389e0d"},
            red: {bg: "#fff1f0", border: "#ff4d4f", icon: "#cf1322"},
            orange: {bg: "#fff7e6", border: "#fa8c16", icon: "#d46b08"},
            blue: {bg: "#e6f7ff", border: "#1890ff", icon: "#0958d9"},
            purple: {bg: "#f9f0ff", border: "#722ed1", icon: "#531dab"},
            gold: {bg: "#fffbe6", border: "#faad14", icon: "#d48806"},
            cyan: {bg: "#e6fffb", border: "#13c2c2", icon: "#08979c"},
            geekblue: {bg: "#f0f5ff", border: "#2f54eb", icon: "#1d39c4"},
            default: {bg: "#fafafa", border: "#d9d9d9", icon: "#595959"},
        };

        return colorMap[color] ?? colorMap.default;
    };

    const dotStyle = getDotStyle(primaryConfig.color);

    /**
     * Loest eine UUID zu einem lesbaren Namen auf
     */
    const resolveUuid = (fieldName: string, uuid: string): string => {
        if (!lookupMaps) {
            return uuid;
        }

        const lookupKey = UUID_LOOKUP_FIELDS[fieldName as keyof typeof UUID_LOOKUP_FIELDS];

        if (!lookupKey) {
            return uuid;
        }

        const lookupMap = lookupMaps[lookupKey];

        return lookupMap[uuid] || uuid;
    };

    /**
     * Rendert einen einzelnen Wert aus oldValues/newValues lesbar
     */
    const renderValue = (fieldName: string, value: unknown): string => {
        if (value === null || value === undefined) {
            return "-";
        }

        // UUID-Felder aufloesen
        if (fieldName in UUID_LOOKUP_FIELDS && typeof value === "string") {
            return resolveUuid(fieldName, value);
        }

        // Datum/Zeit formatieren
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
            return moment(value).format("DD.MM.YYYY HH:mm");
        }

        // Geldbeträge
        if (MONEY_FIELDS.includes(fieldName) && typeof value === "number") {
            return renderAmount({amount: value, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"});
        }

        // PaymentInterval (Kurspreisberechnung)
        if (fieldName === "paymentInterval" && typeof value === "string") {
            if (value === "PerCourse") return "pro Kurs";
            if (value === "PerLesson") return "pro Kurseinheit";
        }

        // Boolean
        if (typeof value === "boolean") {
            return value ? "Ja" : "Nein";
        }

        // Arrays (z.B. Preise, Raten)
        if (Array.isArray(value)) {
            if (value.length === 0) {
                return "-";
            }

            // Versuche eine sinnvolle Zusammenfassung zu erstellen
            return `${value.length} Einträge`;
        }

        // Objekte (z.B. einzelner Preis, einzelne Rate)
        if (typeof value === "object") {
            const obj = value as Record<string, unknown>;

            // Versuche wichtige Felder zu extrahieren
            if ("grossPrice" in obj) {
                const price = obj.grossPrice as number;

                return renderAmount({amount: price, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"});
            }
            if ("installmentAmount" in obj) {
                const amount = obj.installmentAmount as number;

                return renderAmount({amount, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"});
            }
            if ("name" in obj) {
                return String(obj.name);
            }

            // Fallback: Zeige Anzahl der Felder
            return `Objekt (${Object.keys(obj).length} Felder)`;
        }

        return String(value);
    };

    return (
        <>
            <Timeline.Item
                key={`save-op-${saveOp.createDate}`}
                className={css.historyItem}
                dot={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: dotStyle.bg,
                            border: `2px solid ${dotStyle.border}`,
                        }}
                    >
                        <span style={{color: dotStyle.icon, fontSize: "14px", display: "flex", alignItems: "center"}}>
                            <FileTextOutlined />
                        </span>
                    </div>
                }
            >
                {/* Hauptinhalt: Kurs bearbeitet */}
                <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4}}>
                    <Tag
                        icon={<EditOutlined />}
                        color="blue"
                        style={{margin: 0, fontSize: "0.95em", fontWeight: 500}}
                    >
                        Kurs bearbeitet
                    </Tag>
                    <span style={{fontSize: "0.8em", color: "#999"}}>
                        ({actionCount} Bereiche, {totalItems} Änderungen)
                    </span>
                </div>

                {/* Zusammenfassung der Änderungen */}
                <div style={{marginTop: 4, marginBottom: 4}}>
                    {summaries.slice(0, 3).map((summary, idx) => (
                        <div key={idx} style={{fontSize: "0.85em", color: "#555", lineHeight: 1.4}}>
                            • {summary}
                        </div>
                    ))}
                    {summaries.length > 3 && (
                        <div style={{fontSize: "0.85em", color: "#999", fontStyle: "italic"}}>
                            ... und {summaries.length - 3} weitere Änderungen
                        </div>
                    )}
                </div>

                <div className={css.entryMedium + " " + css.grey}>{renderUpdateDate(saveOp.createDate, true)}</div>
                <div className={css.subSubLine}>
                    <TagComponent color="cyan" className={css.subSubLineItem}>
                        {getUserInfo(saveOp.user)}
                    </TagComponent>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setDetailsOpen(true)}
                        style={{padding: 0}}
                        className={css.subSubLineItem}
                    >
                        <InfoCircleOutlined /> Details ({totalItems})
                    </Button>
                </div>
            </Timeline.Item>

            {/* Details-Modal */}
            <Modal
                open={detailsOpen}
                onCancel={() => setDetailsOpen(false)}
                title={
                    <div style={{display: "flex", alignItems: "center", gap: 12}}>
                        <Tag icon={<EditOutlined />} color="blue" style={{margin: 0}}>
                            Kurs bearbeitet
                        </Tag>
                        <span style={{fontWeight: 500}}>
                            {totalItems} Änderungen in {actionCount} Bereichen
                        </span>
                    </div>
                }
                width={900}
                footer={null}
            >
                {/* Meta-Informationen */}
                <div style={{marginBottom: 20, color: "#666", fontSize: "0.9em"}}>
                    {moment(saveOp.createDate).format("DD.MM.YYYY [um] HH:mm [Uhr]")}
                    {saveOp.user && (
                        <span style={{marginLeft: 8}}>
                            - {getUserInfo(saveOp.user)}
                        </span>
                    )}
                </div>

                {/* Gruppierte Änderungen nach Aktionstyp */}
                <div style={{maxHeight: "60vh", overflowY: "auto"}}>
                    {(() => {
                        // Prüfe ob es eine Raten-Neuberechnung gibt (DELETE + ADD gleichzeitig)
                        const ratesDeletedItems = saveOp.actionGroups.get(CourseHistoryAction.InstallmentRatesDeleted) ??
                            saveOp.actionGroups.get(CourseHistoryAction.InstallmentRateDeleted);
                        const ratesAddedItems = saveOp.actionGroups.get(CourseHistoryAction.InstallmentRatesAdded) ??
                            saveOp.actionGroups.get(CourseHistoryAction.InstallmentRateAdded);
                        const hasRatesRecalculation = ratesDeletedItems && ratesDeletedItems.length > 0 &&
                            ratesAddedItems && ratesAddedItems.length > 0;

                        // Aktionen die übersprungen werden sollen (weil kombiniert dargestellt)
                        const skipActions = new Set<CourseHistoryAction>();

                        if (hasRatesRecalculation) {
                            skipActions.add(CourseHistoryAction.InstallmentRatesDeleted);
                            skipActions.add(CourseHistoryAction.InstallmentRateDeleted);
                            skipActions.add(CourseHistoryAction.InstallmentRatesAdded);
                            skipActions.add(CourseHistoryAction.InstallmentRateAdded);
                        }

                        const elements: React.ReactNode[] = [];

                        // Kombinierte Raten-Änderungs-Card zuerst rendern
                        if (hasRatesRecalculation && ratesDeletedItems && ratesAddedItems) {
                            elements.push(
                                <RatesChangedCard
                                    key="rates-changed"
                                    deletedItems={ratesDeletedItems}
                                    addedItems={ratesAddedItems}
                                    currencySettings={currencySettings}
                                />
                            );
                        }

                        // Restliche Action-Groups rendern
                        Array.from(saveOp.actionGroups.entries()).forEach(([action, items]) => {
                            if (skipActions.has(action)) return;

                            const actionConfig = getActionConfig(action);
                            const modalTitle = getModalTitle(action);

                            elements.push(
                                <Card
                                    key={action}
                                    size="small"
                                    title={
                                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                            <Tag icon={actionConfig.icon} color={actionConfig.color}>
                                                {modalTitle}
                                            </Tag>
                                            <span style={{fontWeight: 400, color: "#666"}}>
                                                ({items.length} {items.length === 1 ? "Eintrag" : "Einträge"})
                                            </span>
                                        </div>
                                    }
                                    style={{marginBottom: 16}}
                                >
                                    {renderActionGroupItems(action, items, renderValue, lookupMaps, currencySettings, priceExtractionOptions)}
                                </Card>
                            );
                        });

                        return elements;
                    })()}
                </div>
            </Modal>
        </>
    );
};

// Render-Hilfsfunktion für einzelne oder gruppierte Items
const renderHistoryItemOrGroup = (itemOrGroup: HistoryItemOrGroup, index: number) => {
    if (isSaveOperationGroup(itemOrGroup)) {
        return <SaveOperationGroupItem key={`save-op-${itemOrGroup.createDate}-${index}`} saveOp={itemOrGroup} />;
    }

    if (isGroupedItems(itemOrGroup)) {
        return <GroupedHistoryItem key={`group-${itemOrGroup.createDate}-${index}`} group={itemOrGroup} />;
    }

    return <HistoryItem key={itemOrGroup.id} item={itemOrGroup} />;
};

type CourseHistoryContentProps = {
    history: CourseHistoryByCourseIdQuery["courseHistoryByCourseId"];
    lookupMaps?: EntityLookupMaps;
    /** Anzahl der Kurseinheiten - fuer Preisberechnung bei "pro Kurseinheit" */
    lessonsCount?: number;
    /** Aktueller PaymentInterval des Kurses - "PerCourse" oder "PerLesson" */
    currentPaymentInterval?: PaymentInterval;
};

const CourseHistoryContent: React.FC<CourseHistoryContentProps> = ({history, lookupMaps, lessonsCount, currentPaymentInterval}) => {
    // Deutsche Lokalisierung aktivieren
    moment.locale("de");

    if (!history || history.length === 0) {
        return <Empty description="Keine Historie gefunden" />;
    }

    // Gruppierung der Einträge nach Zeitperioden
    const today = moment().startOf("day");
    const groupedItems = {
        days: {} as Record<string, typeof history>,
        weeks: {} as Record<string, typeof history>,
        months: {} as Record<string, typeof history>,
    };

    // Sortiere Einträge in entsprechende Zeitperioden
    history.forEach((item) => {
        const itemDate = moment(item.createDate);
        const diffDays = today.diff(itemDate, "days");

        if (diffDays < 7) {
            // Letzten 7 Tage - nach Tagen gruppieren
            const dayKey = itemDate.format("DD.MM.YYYY");

            groupedItems.days[dayKey] = [...(groupedItems.days[dayKey] || []), item];
        } else if (diffDays < 30) {
            // Älter als 7 Tage aber neuer als 1 Monat - nach Wochen gruppieren
            const weekKey = `Woche ${itemDate.format("WW, YYYY")}`;

            groupedItems.weeks[weekKey] = [...(groupedItems.weeks[weekKey] || []), item];
        } else {
            // Älter als 1 Monat - nach Monaten gruppieren
            const monthKey = itemDate.format("MMMM YYYY");

            groupedItems.months[monthKey] = [...(groupedItems.months[monthKey] || []), item];
        }
    });

    // Kontext-Wert fuer die Preisberechnung
    const priceCalculationValue: PriceCalculationContext = {
        lessonsCount: lessonsCount ?? 0,
        currentPaymentInterval,
    };

    return (
        <LookupMapsContext.Provider value={lookupMaps}>
        <PriceCalculationContext.Provider value={priceCalculationValue}>
        <div className={css.timelineContainer}>
            {/* Einträge der letzten 7 Tage */}
            {Object.keys(groupedItems.days).length > 0 && (
                <>
                    <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                        {"Letzte 7 Tage"}
                    </Divider>
                    {Object.entries(groupedItems.days).map(([day, items]) => {
                        const groupedDayItems = groupBySaveOperation(items);
                        return (
                            <div key={day} className={css.timelineGroup}>
                                <div className={css.timelineGroupHeader}>{day}</div>
                                <Timeline style={{paddingTop: 8}}>
                                    {groupedDayItems.map((itemOrGroup, idx) => renderHistoryItemOrGroup(itemOrGroup, idx))}
                                </Timeline>
                            </div>
                        );
                    })}
                </>
            )}

            {/* Einträge der letzten Wochen */}
            {Object.keys(groupedItems.weeks).length > 0 && (
                <>
                    <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                        {"Letzte Wochen"}
                    </Divider>
                    {Object.entries(groupedItems.weeks).map(([week, items]) => {
                        const groupedWeekItems = groupBySaveOperation(items);
                        return (
                            <div key={week} className={css.timelineGroup}>
                                <div className={css.timelineGroupHeader}>{week}</div>
                                <Timeline style={{paddingTop: 8}}>
                                    {groupedWeekItems.map((itemOrGroup, idx) => renderHistoryItemOrGroup(itemOrGroup, idx))}
                                </Timeline>
                            </div>
                        );
                    })}
                </>
            )}

            {/* Einträge der letzten Monate */}
            {Object.keys(groupedItems.months).length > 0 && (
                <>
                    <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                        {"Ältere Einträge"}
                    </Divider>
                    {Object.entries(groupedItems.months).map(([month, items]) => {
                        const groupedMonthItems = groupBySaveOperation(items);
                        return (
                            <div key={month} className={css.timelineGroup}>
                                <div className={css.timelineGroupHeader}>{month}</div>
                                <Timeline style={{paddingTop: 8}}>
                                    {groupedMonthItems.map((itemOrGroup, idx) => renderHistoryItemOrGroup(itemOrGroup, idx))}
                                </Timeline>
                            </div>
                        );
                    })}
                </>
            )}
        </div>
        </PriceCalculationContext.Provider>
        </LookupMapsContext.Provider>
    );
};

export default CourseHistoryContent;
